import React, { useState, useEffect, useImperativeHandle, forwardRef, useRef, useCallback } from 'react';
import { X, ChevronDown, ChevronRight, Upload, GripVertical, ArrowUp, ArrowDown, PanelLeftClose, PanelLeftOpen, Pencil, Bold, Italic } from 'lucide-react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import type { InvoiceTemplate, ColumnField } from '@/lib/invoice-template-types';
import { mockPreviewData } from '@/lib/mock-invoice-templates';
import { groupLineItems, GROUP_BY_TOKENS, EXPENSE_TOKENS, PURCHASE_TOKENS, GROUP_TOKEN_META, getDefaultGroupLabelTemplate, resolveGroupLabel, getDescriptionTemplateForGroup } from '@/lib/invoice-group-utils';
import { TokenSidePanel } from '@/components/ui/TokenSidePanel';
import type { TokenGroup } from '@/components/ui/TokenSidePanel';
import { useCompany } from '@/contexts/CompanyContext';
import { toast } from 'sonner';
import { RichTextEditor } from '@/components/ui/RichTextEditor';

import { getMergeTokens } from '@/services/customerTermsService';
import { migrateNotes, getContrastTextColor, FONT_STACKS, hexToRgba } from '@/lib/utils';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import type {
  DragEndEvent,
  DragStartEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// ============================================================================
// Task 3.8 — Grouped Row Description Editor
// A minimal Tiptap editor: bold + italic only (no lists, no headings, no links).
// Exposes an `insertToken(token)` method via ref so token chips can inject text.
// ============================================================================

interface GroupedRowDescriptionEditorProps {
  /** HTML content */
  content: string;
  onChange: (html: string) => void;
  /** Optional className for the outer wrapper */
  className?: string;
}

interface GroupedRowDescriptionEditorHandle {
  insertToken: (token: string) => void;
}

const GroupedRowDescriptionEditor = forwardRef<
  GroupedRowDescriptionEditorHandle,
  GroupedRowDescriptionEditorProps
>(({ content, onChange, className }, ref) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Disable everything except paragraph, bold, italic, text
        heading: false,
        blockquote: false,
        codeBlock: false,
        code: false,
        bulletList: false,
        orderedList: false,
        listItem: false,
        horizontalRule: false,
        strike: false,
      }),
    ],
    content,
    onUpdate({ editor: e }) {
      const html = e.isEmpty ? '' : e.getHTML();
      onChange(html);
    },
    editorProps: {
      attributes: {
        class: [
          'prose prose-sm dark:prose-invert max-w-none',
          'min-h-[48px] px-3 py-2 text-sm outline-none focus:outline-none',
        ].join(' '),
      },
    },
  });

  // Sync external content changes
  React.useEffect(() => {
    if (!editor) return;
    const current = editor.isEmpty ? '' : editor.getHTML();
    if (content !== current) {
      editor.commands.setContent(content ?? '', false);
    }
  }, [content, editor]);

  // Expose insertToken so parent chips can call it
  useImperativeHandle(ref, () => ({
    insertToken(token: string) {
      if (!editor) return;
      editor.chain().focus().insertContent(token).run();
    },
  }));

  if (!editor) return null;

  return (
    <div
      className={[
        'rounded-md border border-input bg-background shadow-xs',
        'focus-within:border-ring focus-within:ring-ring focus-within:ring-[3px]',
        className ?? '',
      ].join(' ')}
    >
      {/* Toolbar — bold & italic only */}
      <div className="flex items-center gap-0.5 border-b border-border px-2 py-1">
        <button
          type="button"
          title="Bold (Ctrl+B)"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={[
            'inline-flex items-center justify-center h-7 w-7 rounded text-xs',
            editor.isActive('bold')
              ? 'bg-primary/10 text-primary'
              : 'text-muted-foreground hover:text-foreground hover:bg-accent',
          ].join(' ')}
        >
          <Bold className="size-3.5" />
        </button>
        <button
          type="button"
          title="Italic (Ctrl+I)"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={[
            'inline-flex items-center justify-center h-7 w-7 rounded text-xs',
            editor.isActive('italic')
              ? 'bg-primary/10 text-primary'
              : 'text-muted-foreground hover:text-foreground hover:bg-accent',
          ].join(' ')}
        >
          <Italic className="size-3.5" />
        </button>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
});

GroupedRowDescriptionEditor.displayName = 'GroupedRowDescriptionEditor';

// ============================================================================

export interface InvoiceTemplateBuilderHandle {
  save: () => void;
}

interface InvoiceTemplateBuilderProps {
  template?: InvoiceTemplate;
  onSave: (template: Partial<InvoiceTemplate>) => void;
  onCancel: () => void;
  onChange?: (template: Partial<InvoiceTemplate>) => void;
}

// Define section types
type SectionId = 'logo' | 'company' | 'client' | 'invoice' | 'project' | 'lineItems' | 'totals' | 'footer' | 'backingSheet';

interface Section {
  id: SectionId;
  title: string;
  required: boolean; // Required sections cannot be disabled
  order: number;
}

// Helper to extract saved toggle/layout states from a template's section configs.
// Handles both builder-saved format (section IDs: logo, company, client, etc.)
// and mock template format (section types: header, summary, line-items, etc.).
function extractTemplateState(template: InvoiceTemplate | undefined) {
  const d = {
    showCompanyLogo: true,
    logoPosition: 'left' as 'left' | 'center' | 'right',
    showCompanyName: true,
    showCompanyAddress: true,
    showCompanyPhone: true,
    showCompanyVAT: true,
    showCompanyRegistration: true,
    companyPosition: 'left' as 'left' | 'center' | 'right',
    showClientName: true,
    showClientAddress: true,
    showClientContact: true,
    clientPosition: 'left' as 'left' | 'center' | 'right',
    showInvoiceNumber: true,
    showInvoiceDate: true,
    showDueDate: true,
    invoiceDetailsPosition: 'right' as 'left' | 'center' | 'right',
    showProjectCode: true,
    showProjectName: true,
    showProjectReference: true,
    showProjectManager: true,
    projectPosition: 'left' as 'left' | 'center' | 'right',
    groupBy: 'none',
    groupedRowDescription: '',
    groupedRowDescriptionExpense: '',
    groupedRowDescriptionPurchase: '',
    includeBackingSheet: false,
    backingSheetOrientation: 'landscape' as 'portrait' | 'landscape',
    backingSheetColumnOrder: null as ColumnField[] | null, // null = inherit from main invoice order on first enable
    backingSheetColumns: null as Partial<Record<ColumnField, boolean>> | null, // null = all visible by default
    alternateRowColors: false, // Task 6.4b
    showBorders: true,         // Task 6.4d
    showDate: true,
    showDescription: true,
    showCostCentreCode: true,
    showCostCentreName: true,
    showPerson: true,
    showRole: true,
    showQty: true,
    showRate: true,
    showVAT: true,
    showVATCode: true,
    showAmount: true,
    showType: false,
    showSubtotal: true,
    showTax: true,
    showDiscount: true,
    showTotal: true,
    showBankDetails: false,
    showNotes: true,
    showTermsConditions: true,
    footerPosition: 'left' as 'left' | 'center' | 'right',
    notesContent: '',
    termsContent: '',
    // Backing Sheet independent header
    bsShowLogo: true,
    bsShowCompanyName: true,
    bsShowCompanyAddress: false,
    bsShowInvoiceNumber: true,
    bsShowInvoiceDate: true,
    bsShowClientName: true,
    bsShowClientAddress: false,
    // Backing Sheet independent footer
    bsShowBankDetails: false,
    bsShowNotes: false,
    bsShowTermsConditions: false,
  };

  if (!template?.sections) return d;

  const findById = (id: string) => template.sections.find(s => s.id === id);
  const findByType = (type: string) => template.sections.find(s => s.type === type);

  // --- Logo ---
  const logoSec = findById('logo');
  if (logoSec?.config) {
    const c = logoSec.config as unknown as Record<string, unknown>;
    if ('showLogo' in c) d.showCompanyLogo = c.showLogo as boolean;
    if ('showCompanyLogo' in c) d.showCompanyLogo = c.showCompanyLogo as boolean;
    if ('logoPosition' in c) d.logoPosition = c.logoPosition as typeof d.logoPosition;
  }

  // --- Company (builder format: id='company') ---
  const companySec = findById('company');
  if (companySec?.config) {
    const c = companySec.config as unknown as Record<string, unknown>;
    if ('showCompanyName' in c) d.showCompanyName = c.showCompanyName as boolean;
    if ('showCompanyAddress' in c) d.showCompanyAddress = c.showCompanyAddress as boolean;
    if ('showPhone' in c) d.showCompanyPhone = c.showPhone as boolean;
    if ('showCompanyPhone' in c) d.showCompanyPhone = c.showCompanyPhone as boolean;
    if ('showVAT' in c) d.showCompanyVAT = c.showVAT as boolean;
    if ('showRegistration' in c) d.showCompanyRegistration = c.showRegistration as boolean;
    if ('position' in c) d.companyPosition = c.position as typeof d.companyPosition;
  }

  // --- Client (builder format: id='client') ---
  const clientSec = findById('client');
  if (clientSec?.config) {
    const c = clientSec.config as unknown as Record<string, unknown>;
    if ('showClientName' in c) d.showClientName = c.showClientName as boolean;
    if ('showClientAddress' in c) d.showClientAddress = c.showClientAddress as boolean;
    if ('showClientContact' in c) d.showClientContact = c.showClientContact as boolean;
    if ('position' in c) d.clientPosition = c.position as typeof d.clientPosition;
  }

  // --- Invoice (builder format: id='invoice') ---
  const invoiceSec = findById('invoice');
  if (invoiceSec?.config) {
    const c = invoiceSec.config as unknown as Record<string, unknown>;
    if ('showInvoiceNumber' in c) d.showInvoiceNumber = c.showInvoiceNumber as boolean;
    if ('showInvoiceDate' in c) d.showInvoiceDate = c.showInvoiceDate as boolean;
    if ('showDueDate' in c) d.showDueDate = c.showDueDate as boolean;
    if ('position' in c) d.invoiceDetailsPosition = c.position as typeof d.invoiceDetailsPosition;
  }

  // --- Header (mock format: type='header' — combines company/client/invoice) ---
  const headerSec = findByType('header');
  if (headerSec?.config && !companySec && !clientSec && !invoiceSec) {
    const c = headerSec.config as unknown as Record<string, unknown>;
    if ('showCompanyName' in c) d.showCompanyName = c.showCompanyName as boolean;
    if ('showCompanyAddress' in c) d.showCompanyAddress = c.showCompanyAddress as boolean;
    if ('showCompanyPhone' in c) d.showCompanyPhone = c.showCompanyPhone as boolean;
    if ('showVAT' in c) d.showCompanyVAT = c.showVAT as boolean;
    if ('showRegistration' in c) d.showCompanyRegistration = c.showRegistration as boolean;
    if ('showClientName' in c) d.showClientName = c.showClientName as boolean;
    if ('showClientAddress' in c) d.showClientAddress = c.showClientAddress as boolean;
    if ('showClientContact' in c) d.showClientContact = c.showClientContact as boolean;
    if ('showInvoiceNumber' in c) d.showInvoiceNumber = c.showInvoiceNumber as boolean;
    if ('showInvoiceDate' in c) d.showInvoiceDate = c.showInvoiceDate as boolean;
    if ('showDueDate' in c) d.showDueDate = c.showDueDate as boolean;
  }

  // --- Project (builder: id='project') or Summary (mock: type='summary') ---
  const projectSec = findById('project') || findByType('summary');
  if (projectSec?.config) {
    const c = projectSec.config as unknown as Record<string, unknown>;
    if ('showProjectCode' in c) d.showProjectCode = c.showProjectCode as boolean;
    if ('showProjectName' in c) d.showProjectName = c.showProjectName as boolean;
    if ('showProjectReference' in c) d.showProjectReference = c.showProjectReference as boolean;
    if ('showProjectDescription' in c) d.showProjectReference = c.showProjectDescription as boolean; // Legacy: maps description toggle to reference for builder state
    if ('showProjectManager' in c) d.showProjectManager = c.showProjectManager as boolean;
    if ('position' in c) d.projectPosition = c.position as typeof d.projectPosition;
  }

  // --- Line Items ---
  const lineItemsSec = findById('lineItems') || findByType('line-items');
  if (lineItemsSec?.config) {
    const c = lineItemsSec.config as unknown as Record<string, unknown>;
    if ('groupBy' in c) d.groupBy = c.groupBy as string;
    if ('groupedRowDescription' in c) d.groupedRowDescription = c.groupedRowDescription as string;
    if ('groupedRowDescriptionExpense' in c) d.groupedRowDescriptionExpense = c.groupedRowDescriptionExpense as string;
    if ('groupedRowDescriptionPurchase' in c) d.groupedRowDescriptionPurchase = c.groupedRowDescriptionPurchase as string;
    if ('includeBackingSheet' in c) d.includeBackingSheet = c.includeBackingSheet as boolean;
    if ('backingSheetOrientation' in c) d.backingSheetOrientation = c.backingSheetOrientation as 'portrait' | 'landscape';
    if ('backingSheetColumnOrder' in c && Array.isArray(c.backingSheetColumnOrder)) d.backingSheetColumnOrder = c.backingSheetColumnOrder as ColumnField[];
    if ('backingSheetColumns' in c && c.backingSheetColumns && typeof c.backingSheetColumns === 'object') d.backingSheetColumns = c.backingSheetColumns as Partial<Record<ColumnField, boolean>>;
    if ('alternateRowColors' in c) d.alternateRowColors = c.alternateRowColors as boolean; // Task 6.4b
    if ('showBorders' in c) d.showBorders = c.showBorders as boolean;                      // Task 6.4d
    if ('columns' in c && c.columns) {
      if (Array.isArray(c.columns)) {
        // Mock/builder array format: [{ id, field, visible, ... }]
        // Reset ALL column flags to false first — only columns explicitly
        // present in the array (with visible: true) will be shown.
        d.showDate = false; d.showDescription = false; d.showCostCentreCode = false;
        d.showCostCentreName = false; d.showPerson = false; d.showRole = false;
        d.showQty = false; d.showRate = false; d.showVAT = false;
        d.showVATCode = false; d.showAmount = false;
        type ColEntry = { id?: string; field?: string; visible?: boolean };
        (c.columns as ColEntry[]).forEach((col) => {
          const key = col.field ?? col.id ?? '';
          const show = col.visible !== false;
          const fieldMap: Record<string, () => void> = {
            date:           () => { d.showDate           = show; },
            description:    () => { d.showDescription    = show; },
            costCentreCode: () => { d.showCostCentreCode = show; },
            costCentreName: () => { d.showCostCentreName = show; },
            person:         () => { d.showPerson         = show; },
            role:           () => { d.showRole           = show; },
            qty:            () => { d.showQty            = show; },
            rate:           () => { d.showRate           = show; },
            net:            () => { d.showRate           = show; },
            vat:            () => { d.showVAT            = show; },
            vatCode:        () => { d.showVATCode        = show; },
            amount:         () => { d.showAmount         = show; },
            type:           () => { d.showType           = show; },
          };
          fieldMap[key]?.();
        });
      } else if (typeof c.columns === 'object') {
        // Legacy object/dictionary format: { date: true, role: false, ... }
        const cols = c.columns as Record<string, boolean>;
        if ('date' in cols) d.showDate = cols.date;
        if ('description' in cols) d.showDescription = cols.description;
        if ('costCentreCode' in cols) d.showCostCentreCode = cols.costCentreCode;
        if ('costCentreName' in cols) d.showCostCentreName = cols.costCentreName;
        if ('person' in cols) d.showPerson = cols.person;
        if ('role' in cols) d.showRole = cols.role;
        if ('qty' in cols) d.showQty = cols.qty;
        if ('rate' in cols) d.showRate = cols.rate;
        if ('vat' in cols) d.showVAT = cols.vat;
        if ('vatCode' in cols) d.showVATCode = cols.vatCode;
        if ('amount' in cols) d.showAmount = cols.amount;
        if ('type' in cols) d.showType = cols.type;
      }
    }
  }

  // --- Totals ---
  const totalsSec = findById('totals') || findByType('totals');
  if (totalsSec?.config) {
    const c = totalsSec.config as unknown as Record<string, unknown>;
    if ('showSubtotal' in c) d.showSubtotal = c.showSubtotal as boolean;
    if ('showTax' in c) d.showTax = c.showTax as boolean;
    if ('showDiscount' in c) d.showDiscount = c.showDiscount as boolean;
    if ('showTotal' in c) d.showTotal = c.showTotal as boolean;
  }

  // --- Footer ---
  const footerSec = findById('footer') || findByType('footer');
  if (footerSec?.config) {
    const c = footerSec.config as unknown as Record<string, unknown>;
    if ('showBankDetails' in c) d.showBankDetails = c.showBankDetails as boolean;
    if ('showNotes' in c) d.showNotes = c.showNotes as boolean;
    if ('showTermsConditions' in c) d.showTermsConditions = c.showTermsConditions as boolean;
    if ('showTermsAndConditions' in c) d.showTermsConditions = c.showTermsAndConditions as boolean;
    if ('notesContent' in c) d.notesContent = migrateNotes(c.notesContent as string);
    if ('termsContent' in c) d.termsContent = c.termsContent as string;
    if ('position' in c) d.footerPosition = c.position as typeof d.footerPosition;
  }

  // --- Backing Sheet header/footer (builder format: id='lineItems') ---
  const bsSec = findById('lineItems');
  if (bsSec?.config) {
    const c = bsSec.config as unknown as Record<string, unknown>;
    if ('bsShowLogo' in c) d.bsShowLogo = c.bsShowLogo as boolean;
    if ('bsShowCompanyName' in c) d.bsShowCompanyName = c.bsShowCompanyName as boolean;
    if ('bsShowCompanyAddress' in c) d.bsShowCompanyAddress = c.bsShowCompanyAddress as boolean;
    if ('bsShowInvoiceNumber' in c) d.bsShowInvoiceNumber = c.bsShowInvoiceNumber as boolean;
    if ('bsShowInvoiceDate' in c) d.bsShowInvoiceDate = c.bsShowInvoiceDate as boolean;
    if ('bsShowClientName' in c) d.bsShowClientName = c.bsShowClientName as boolean;
    if ('bsShowClientAddress' in c) d.bsShowClientAddress = c.bsShowClientAddress as boolean;
    if ('bsShowBankDetails' in c) d.bsShowBankDetails = c.bsShowBankDetails as boolean;
    if ('bsShowNotes' in c) d.bsShowNotes = c.bsShowNotes as boolean;
    if ('bsShowTermsConditions' in c) d.bsShowTermsConditions = c.bsShowTermsConditions as boolean;
  }

  // Override logo position from branding
  if (template.branding?.logoPosition) {
    d.logoPosition = template.branding.logoPosition;
  }

  // Override groupBy from settings
  if (template.settings?.groupBy) {
    d.groupBy = template.settings.groupBy;
  }

  return d;
}

// Helper to restore section ordering from a previously-saved template
function extractSectionOrder(template: InvoiceTemplate | undefined): Section[] {
  const defaultSections: Section[] = [
    { id: 'logo', title: 'Company Logo', required: false, order: 0 },
    { id: 'company', title: 'Your Company', required: false, order: 1 },
    { id: 'client', title: 'Client', required: false, order: 2 },
    { id: 'invoice', title: 'Invoice Details', required: false, order: 3 },
    { id: 'project', title: 'Project', required: false, order: 4 },
    { id: 'lineItems', title: 'Line Items', required: false, order: 5 },
    { id: 'totals', title: 'Totals', required: false, order: 6 },
    { id: 'footer', title: 'Footer', required: false, order: 7 },
  ];

  if (!template?.sections) return defaultSections;

  // Check if sections use builder format (IDs match builder section IDs)
  const builderIds = new Set<string>(['logo', 'company', 'client', 'invoice', 'project', 'lineItems', 'totals', 'footer']);
  const hasBuilderFormat = template.sections.some(s => builderIds.has(s.id));

  if (hasBuilderFormat) {
    const sectionMap = new Map(template.sections.map(s => [s.id, s.order]));
    return defaultSections
      .map(s => ({ ...s, order: sectionMap.get(s.id) ?? s.order }))
      .sort((a, b) => a.order - b.order);
  }

  return defaultSections;
}

// Module-level types for the Backing Sheet preview column definitions (Task 5.3).
// Declared here rather than inside the component body because Babel's JSX parser
// does not support `type` aliases inside function bodies.
type BsPreviewItem = (typeof mockPreviewData.lineItems)[number];
type BsColDef = { field: string; label: string; align: 'left' | 'right'; renderCell: (item: BsPreviewItem) => React.ReactNode };

// Module-level map of backing sheet column field → label.
// Kept outside the component to avoid Babel's restriction on typed declarations
// inside function/switch bodies.
const BS_COLUMN_LABELS: Record<string, string> = {
  date:           'Date',
  description:    'Description',
  person:         'Person',
  role:           'Role',
  qty:            'Qty',
  net:            'Rate',
  vat:            'VAT',
  vatCode:        'VAT Code',
  amount:         'Amount',
  costCentreCode: 'Cost Centre Code',
  costCentreName: 'Cost Centre Name',
  type:           'Type',
};

// ── BackingSheetPanel ─────────────────────────────────────────────────────────
// Extracted as a module-level component so that Babel (used by Vite's HMR) never
// encounters TypeScript-annotated declarations inside a switch/function body.
interface BackingSheetPanelProps {
  includeBackingSheet: boolean;
  setIncludeBackingSheet: (v: boolean) => void;
  backingSheetOrientation: 'portrait' | 'landscape';
  setBackingSheetOrientation: (v: 'portrait' | 'landscape') => void;
  backingSheetColumnOrder: ColumnField[];
  setBackingSheetColumnOrder: React.Dispatch<React.SetStateAction<ColumnField[]>>;
  backingSheetColumns: Partial<Record<ColumnField, boolean>>;
  setBackingSheetColumns: React.Dispatch<React.SetStateAction<Partial<Record<ColumnField, boolean>>>>;
  activeBsColumnDragId: string | null;
  setActiveBsColumnDragId: (v: string | null) => void;
  sensors: ReturnType<typeof useSensors>;
  // Header toggles
  bsShowLogo: boolean; setBsShowLogo: (v: boolean) => void;
  bsShowCompanyName: boolean; setBsShowCompanyName: (v: boolean) => void;
  bsShowCompanyAddress: boolean; setBsShowCompanyAddress: (v: boolean) => void;
  bsShowInvoiceNumber: boolean; setBsShowInvoiceNumber: (v: boolean) => void;
  bsShowInvoiceDate: boolean; setBsShowInvoiceDate: (v: boolean) => void;
  bsShowClientName: boolean; setBsShowClientName: (v: boolean) => void;
  bsShowClientAddress: boolean; setBsShowClientAddress: (v: boolean) => void;
  // Footer toggles
  bsShowBankDetails: boolean; setBsShowBankDetails: (v: boolean) => void;
  bsShowNotes: boolean; setBsShowNotes: (v: boolean) => void;
  bsShowTermsConditions: boolean; setBsShowTermsConditions: (v: boolean) => void;
}

function BackingSheetPanel({
  includeBackingSheet,
  setIncludeBackingSheet,
  backingSheetOrientation,
  setBackingSheetOrientation,
  backingSheetColumnOrder,
  setBackingSheetColumnOrder,
  backingSheetColumns,
  setBackingSheetColumns,
  activeBsColumnDragId,
  setActiveBsColumnDragId,
  sensors,
  bsShowLogo, setBsShowLogo,
  bsShowCompanyName, setBsShowCompanyName,
  bsShowCompanyAddress, setBsShowCompanyAddress,
  bsShowInvoiceNumber, setBsShowInvoiceNumber,
  bsShowInvoiceDate, setBsShowInvoiceDate,
  bsShowClientName, setBsShowClientName,
  bsShowClientAddress, setBsShowClientAddress,
  bsShowBankDetails, setBsShowBankDetails,
  bsShowNotes, setBsShowNotes,
  bsShowTermsConditions, setBsShowTermsConditions,
}: BackingSheetPanelProps) {
  return (
    <div className="space-y-4">
      {/* Enable toggle */}
      <div className="flex items-center justify-between py-1">
        <div>
          <Label htmlFor="backing-sheet-toggle" className="cursor-pointer text-sm font-normal">
            Include Backing Sheet
          </Label>
          <p className="text-xs text-muted-foreground mt-0.5">
            Generates a second page with full line-by-line detail.
          </p>
        </div>
        <Switch
          id="backing-sheet-toggle"
          checked={includeBackingSheet}
          onCheckedChange={setIncludeBackingSheet}
        />
      </div>

      {includeBackingSheet && (
        <div className="space-y-4">
          {/* Orientation */}
          <div className="flex items-center justify-between">
            <Label className="text-sm font-normal">Orientation</Label>
            <Select
              value={backingSheetOrientation}
              onValueChange={(v) => setBackingSheetOrientation(v as 'portrait' | 'landscape')}
            >
              <SelectTrigger className="w-[160px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="portrait">Portrait</SelectItem>
                <SelectItem value="landscape">Landscape</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Header toggles */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground border-b pb-1 mb-2">Header</p>
            <div className="space-y-0.5">
              <FieldToggle label="Show Logo"            checked={bsShowLogo}            onCheckedChange={setBsShowLogo} />
              <FieldToggle label="Show Company Name"    checked={bsShowCompanyName}     onCheckedChange={setBsShowCompanyName} />
              <FieldToggle label="Show Company Address" checked={bsShowCompanyAddress}  onCheckedChange={setBsShowCompanyAddress} />
              <FieldToggle label="Show Invoice Number"  checked={bsShowInvoiceNumber}   onCheckedChange={setBsShowInvoiceNumber} />
              <FieldToggle label="Show Invoice Date"    checked={bsShowInvoiceDate}     onCheckedChange={setBsShowInvoiceDate} />
              <FieldToggle label="Show Client Name"     checked={bsShowClientName}      onCheckedChange={setBsShowClientName} />
              <FieldToggle label="Show Client Address"  checked={bsShowClientAddress}   onCheckedChange={setBsShowClientAddress} />
            </div>
          </div>

          {/* Independent column list with drag-and-drop */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground border-b pb-1 mb-2">Columns — drag to reorder</p>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={(e) => setActiveBsColumnDragId(e.active.id as string)}
              onDragEnd={(e) => {
                const { active, over } = e;
                setActiveBsColumnDragId(null);
                if (over && active.id !== over.id) {
                  setBackingSheetColumnOrder((prev) => {
                    const oldIdx = prev.indexOf(active.id as ColumnField);
                    const newIdx = prev.indexOf(over.id as ColumnField);
                    return arrayMove(prev, oldIdx, newIdx);
                  });
                }
              }}
            >
              <SortableContext items={backingSheetColumnOrder} strategy={verticalListSortingStrategy}>
                <div className="space-y-0.5">
                  {backingSheetColumnOrder.map((field) => {
                    const label = BS_COLUMN_LABELS[field] ?? field;
                    const checked = backingSheetColumns[field] !== false;
                    return (
                      <SortableColumnRow
                        key={field}
                        id={field}
                        label={label}
                        checked={checked}
                        onCheckedChange={(v) =>
                          setBackingSheetColumns((prev) => ({ ...prev, [field]: v }))
                        }
                        disabled={false}
                      />
                    );
                  })}
                </div>
              </SortableContext>
              <DragOverlay>
                {activeBsColumnDragId ? (
                  <div className="flex items-center gap-2 border rounded bg-background shadow px-2 py-1.5 text-sm opacity-90">
                    <GripVertical className="h-3.5 w-3.5 text-muted-foreground" />
                    <span>{BS_COLUMN_LABELS[activeBsColumnDragId] ?? activeBsColumnDragId}</span>
                  </div>
                ) : null}
              </DragOverlay>
            </DndContext>
          </div>

          {/* Footer toggles */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground border-b pb-1 mb-2">Footer</p>
            <div className="space-y-0.5">
              <FieldToggle label="Show Bank Details"          checked={bsShowBankDetails}    onCheckedChange={setBsShowBankDetails} />
              <FieldToggle label="Show Notes"                 checked={bsShowNotes}          onCheckedChange={setBsShowNotes} />
              <FieldToggle label="Show Terms & Conditions"    checked={bsShowTermsConditions} onCheckedChange={setBsShowTermsConditions} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export const InvoiceTemplateBuilder = forwardRef<InvoiceTemplateBuilderHandle, InvoiceTemplateBuilderProps>(function InvoiceTemplateBuilder({
  template,
  onSave,
  onCancel,
  onChange,
}, ref) {
  // Extract saved state from template sections so edits are restored on reopen
  const init = extractTemplateState(template);

  // Default column order — Task 2.7a
  const DEFAULT_COLUMN_ORDER: ColumnField[] = [
    'date', 'description', 'person', 'role', 'qty', 'net', 'vat', 'vatCode', 'amount',
    'costCentreCode', 'costCentreName', 'type',
  ];

  // Live company details from context — used in preview rendering (1.13a)
  const { currentCompany } = useCompany();

  const [name, setName] = useState(template?.name || '');
  const [description, setDescription] = useState(template?.description || '');
  
  // Section ordering state - restored from template if previously saved
  const [sections, setSections] = useState<Section[]>(() => extractSectionOrder(template));

  const [activeDragId, setActiveDragId] = useState<SectionId | null>(null);
  
  // Section collapse states
  const [logoExpanded, setLogoExpanded] = useState(true);
  const [companyExpanded, setCompanyExpanded] = useState(true);
  const [clientExpanded, setClientExpanded] = useState(true);
  const [invoiceExpanded, setInvoiceExpanded] = useState(true);
  const [projectExpanded, setProjectExpanded] = useState(true);
  const [lineItemsExpanded, setLineItemsExpanded] = useState(true);
  const [totalsExpanded, setTotalsExpanded] = useState(true);
  const [footerExpanded, setFooterExpanded] = useState(true);
  const [backingSheetExpanded, setBackingSheetExpanded] = useState(false);
  
  // Company fields - initialized from saved template config
  const [showCompanyLogo, setShowCompanyLogo] = useState(init.showCompanyLogo);
  const [companyLogo, setCompanyLogo] = useState(template?.branding?.logo || '');
  const [logoPosition, setLogoPosition] = useState<'left' | 'center' | 'right'>(init.logoPosition);
  const [showCompanyName, setShowCompanyName] = useState(init.showCompanyName);
  const [showCompanyAddress, setShowCompanyAddress] = useState(init.showCompanyAddress);
  const [showCompanyPhone, setShowCompanyPhone] = useState(init.showCompanyPhone);
  const [showCompanyVAT, setShowCompanyVAT] = useState(init.showCompanyVAT);
  const [showCompanyRegistration, setShowCompanyRegistration] = useState(init.showCompanyRegistration);
  const [companyPosition, setCompanyPosition] = useState<'left' | 'center' | 'right'>(init.companyPosition);
  
  // Client fields
  const [showClientName, setShowClientName] = useState(init.showClientName);
  const [showClientAddress, setShowClientAddress] = useState(init.showClientAddress);
  const [showClientContact, setShowClientContact] = useState(init.showClientContact);
  const [clientPosition, setClientPosition] = useState<'left' | 'center' | 'right'>(init.clientPosition);
  
  // Invoice details
  const [showInvoiceNumber, setShowInvoiceNumber] = useState(init.showInvoiceNumber);
  const [showInvoiceDate, setShowInvoiceDate] = useState(init.showInvoiceDate);
  const [showDueDate, setShowDueDate] = useState(init.showDueDate);
  const [invoiceDetailsPosition, setInvoiceDetailsPosition] = useState<'left' | 'center' | 'right'>(init.invoiceDetailsPosition);

  
  // Project fields
  const [showProjectCode, setShowProjectCode] = useState(init.showProjectCode);
  const [showProjectName, setShowProjectName] = useState(init.showProjectName);
  const [showProjectReference, setShowProjectReference] = useState(init.showProjectReference);
  const [showProjectManager, setShowProjectManager] = useState(init.showProjectManager);
  const [projectPosition, setProjectPosition] = useState<'left' | 'center' | 'right'>(init.projectPosition);
  
  // Page Layout — Task 2.2 / Task 2.1
  const [orientation, setOrientation] = useState<'portrait' | 'landscape'>(
    (template?.settings?.orientation as 'portrait' | 'landscape') || 'portrait'
  );

  // Column order — Task 2.7a
  type ColumnFieldType = typeof DEFAULT_COLUMN_ORDER[number];
  const [columnOrder, setColumnOrder] = useState<ColumnFieldType[]>(() => {
    // Try to restore from template (Task 2.8 write-path)
    const lineItemsSec = template?.sections?.find(
      s => s.id === 'lineItems' || s.type === 'line-items',
    );
    if (lineItemsSec?.config) {
      const c = lineItemsSec.config as unknown as Record<string, unknown>;
      if (Array.isArray(c.columnOrder) && c.columnOrder.length > 0) {
        const saved = c.columnOrder as ColumnFieldType[];
        // Migration: append any new fields not in the saved order
        const missing = DEFAULT_COLUMN_ORDER.filter(f => !saved.includes(f));
        return missing.length > 0 ? [...saved, ...missing] : saved;
      }
    }
    return DEFAULT_COLUMN_ORDER;
  });

  // DnD for columns — Task 2.7b
  const [activeColumnDragId, setActiveColumnDragId] = useState<string | null>(null);

  // Line items
  const [groupBy, setGroupBy] = useState<string>(init.groupBy);
  const [includeBackingSheet, setIncludeBackingSheet] = useState<boolean>(init.includeBackingSheet ?? false);
  const [backingSheetOrientation, setBackingSheetOrientation] = useState<'portrait' | 'landscape'>(init.backingSheetOrientation ?? 'landscape');
  // Backing Sheet column order — initialised lazily from main invoice columnOrder on first enable
  const [backingSheetColumnOrder, setBackingSheetColumnOrder] = useState<ColumnField[]>(() => {
    const saved = init.backingSheetColumnOrder ?? [...DEFAULT_COLUMN_ORDER];
    // Migration: append any new fields not in the saved order
    const missing = DEFAULT_COLUMN_ORDER.filter(f => !saved.includes(f));
    return missing.length > 0 ? [...saved, ...missing] : saved;
  });
  // Backing Sheet column visibility — all true by default
  const [backingSheetColumns, setBackingSheetColumns] = useState<Partial<Record<ColumnField, boolean>>>(() => {
    const saved = init.backingSheetColumns ?? {};
    // Migration: add any new fields not in saved visibility map (default true)
    const migrated = { ...saved };
    DEFAULT_COLUMN_ORDER.forEach(f => { if (!(f in migrated)) migrated[f] = true; });
    return migrated;
  });
  const [activeBsColumnDragId, setActiveBsColumnDragId] = useState<string | null>(null);

  // Backing Sheet independent header
  const [bsShowLogo, setBsShowLogo] = useState(init.bsShowLogo);
  const [bsShowCompanyName, setBsShowCompanyName] = useState(init.bsShowCompanyName);
  const [bsShowCompanyAddress, setBsShowCompanyAddress] = useState(init.bsShowCompanyAddress);
  const [bsShowInvoiceNumber, setBsShowInvoiceNumber] = useState(init.bsShowInvoiceNumber);
  const [bsShowInvoiceDate, setBsShowInvoiceDate] = useState(init.bsShowInvoiceDate);
  const [bsShowClientName, setBsShowClientName] = useState(init.bsShowClientName);
  const [bsShowClientAddress, setBsShowClientAddress] = useState(init.bsShowClientAddress);
  // Backing Sheet independent footer
  const [bsShowBankDetails, setBsShowBankDetails] = useState(init.bsShowBankDetails);
  const [bsShowNotes, setBsShowNotes] = useState(init.bsShowNotes);
  const [bsShowTermsConditions, setBsShowTermsConditions] = useState(init.bsShowTermsConditions);
  const [groupedRowDescription, setGroupedRowDescription] = useState<string>(init.groupedRowDescription);
  const [groupedRowDescriptionExpense, setGroupedRowDescriptionExpense] = useState<string>(init.groupedRowDescriptionExpense ?? '');
  const [groupedRowDescriptionPurchase, setGroupedRowDescriptionPurchase] = useState<string>(init.groupedRowDescriptionPurchase ?? '');
  const [descDialogOpen, setDescDialogOpen] = useState(false);
  const [descActiveTab, setDescActiveTab] = useState<'timesheet' | 'expense' | 'purchase'>('timesheet');
  const [descDraftTimesheet, setDescDraftTimesheet] = useState(init.groupedRowDescription);
  const [descDraftExpense, setDescDraftExpense] = useState(init.groupedRowDescriptionExpense ?? '');
  const [descDraftPurchase, setDescDraftPurchase] = useState(init.groupedRowDescriptionPurchase ?? '');
  // Keep legacy alias for parts of the code that still reference descDraft
  const descDraft = descActiveTab === 'expense' ? descDraftExpense : descActiveTab === 'purchase' ? descDraftPurchase : descDraftTimesheet;
  // Ref to the GroupedRowDescriptionEditor so token chips can call insertToken()
  const descEditorRef = useRef<GroupedRowDescriptionEditorHandle>(null);

  // When groupBy changes, set a sensible default description if the field is empty
  // or still holds the default for a different groupBy option.
  useEffect(() => {
    if (groupBy === 'none') return;
    const allDefaults = Object.values(
      { person: '{person}', role: '{role}', phase: '{phase}', task: '{task}', 'cost-center': '{costCentreName}' }
    );
    const newDefault = getDefaultGroupLabelTemplate(groupBy as 'person' | 'role' | 'phase' | 'task' | 'cost-center');
    setGroupedRowDescription(prev => {
      const stripped = prev.replace(/<[^>]*>/g, '').trim(); // strip HTML tags for comparison
      // Only auto-populate if empty or currently showing another groupBy's bare default
      if (!stripped || allDefaults.includes(stripped)) {
        return `<p>${newDefault}</p>`;
      }
      return prev;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupBy]);

  const [showDate, setShowDate] = useState(init.showDate);
  const [showDescription, setShowDescription] = useState(init.showDescription);
  const [showCostCentreCode, setShowCostCentreCode] = useState(init.showCostCentreCode);
  const [showCostCentreName, setShowCostCentreName] = useState(init.showCostCentreName);
  const [showPerson, setShowPerson] = useState(init.showPerson);
  const [showRole, setShowRole] = useState(init.showRole);
  const [showQty, setShowQty] = useState(init.showQty);
  const [showRate, setShowRate] = useState(init.showRate);
  const [showVAT, setShowVAT] = useState(init.showVAT);
  const [showVATCode, setShowVATCode] = useState(init.showVATCode);
  const [showAmount, setShowAmount] = useState(init.showAmount);
  const [showType, setShowType] = useState(init.showType);
  
  // Totals
  const [showSubtotal, setShowSubtotal] = useState(init.showSubtotal);
  const [showTax, setShowTax] = useState(init.showTax);
  const [showDiscount, setShowDiscount] = useState(init.showDiscount);
  const [showTotal, setShowTotal] = useState(init.showTotal);
  
  // Appearance — Branding (Task 6.3)
  const [primaryColor, setPrimaryColor] = useState<string>(
    template?.branding?.primaryColor || '#2563eb'
  );
  const [fontFamily, setFontFamily] = useState<string>(
    template?.branding?.fontFamily || 'inter'
  );

  // Appearance — Table Style (Task 6.4)
  const [alternateRowColors, setAlternateRowColors] = useState<boolean>(init.alternateRowColors);
  const [showBorders, setShowBorders] = useState<boolean>(init.showBorders);

  // Footer
  const [showBankDetails, setShowBankDetails] = useState(init.showBankDetails);
  const [showNotes, setShowNotes] = useState(init.showNotes);
  const [showTermsConditions, setShowTermsConditions] = useState(init.showTermsConditions);
  const [footerPosition, setFooterPosition] = useState<'left' | 'center' | 'right'>(init.footerPosition);
  
  // Notes rich text content (persisted, VARCHAR(1000) equivalent)
  const NOTES_MAX_LENGTH = 1000;
  const [notesContent, setNotesContent] = useState(init.notesContent);
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [notesDraft, setNotesDraft] = useState(init.notesContent);
  // Terms & Conditions rich text content
  const TERMS_MAX_LENGTH = 2000;
  const [termsContent, setTermsContent] = useState(init.termsContent);
  const [termsDialogOpen, setTermsDialogOpen] = useState(false);
  const [termsDraft, setTermsDraft] = useState(init.termsContent);
  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement required before drag starts
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveDragId(event.active.id as SectionId);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    setActiveDragId(null);

    if (over && active.id !== over.id) {
      setSections((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        
        const newItems = arrayMove(items, oldIndex, newIndex);
        
        // Update order property
        return newItems.map((item, index) => ({
          ...item,
          order: index,
        }));
      });
      
      toast.success('Section order updated');
    }
  };

  // Keyboard accessibility - move section up/down
  const moveSectionUp = (sectionId: SectionId) => {
    setSections((items) => {
      const index = items.findIndex((item) => item.id === sectionId);
      if (index > 0) {
        const newItems = arrayMove(items, index, index - 1);
        return newItems.map((item, idx) => ({
          ...item,
          order: idx,
        }));
      }
      return items;
    });
    toast.success('Section moved up');
  };

  const moveSectionDown = (sectionId: SectionId) => {
    setSections((items) => {
      const index = items.findIndex((item) => item.id === sectionId);
      if (index < items.length - 1) {
        const newItems = arrayMove(items, index, index + 1);
        return newItems.map((item, idx) => ({
          ...item,
          order: idx,
        }));
      }
      return items;
    });
    toast.success('Section moved down');
  };

  const handleSave = () => {
    const templateData: Partial<InvoiceTemplate> = {
      id: template?.id,
      name,
      description,
      branding: {
        logo: companyLogo,
        primaryColor, // Task 6.3b
        secondaryColor: template?.branding?.secondaryColor || '#64748b',
        fontFamily, // Task 6.3e
        logoPosition: logoPosition,
        textColor: template?.branding?.textColor || '#1e293b',
        fontSize: template?.branding?.fontSize || 14,
      },
      sections: sections.map((section) => ({
        id: section.id,
        type: section.id === 'lineItems' ? 'line-items' as const : section.id === 'invoice' ? 'header' as const : section.id === 'project' ? 'summary' as const : section.id as 'header' | 'summary' | 'line-items' | 'totals' | 'footer' | 'custom',
        visible: true,
        order: section.order,
        config: getSectionConfig(section.id),
      })) as InvoiceTemplate['sections'],
      settings: {
        ...template?.settings,
        groupBy: groupBy as InvoiceTemplate['settings']['groupBy'],
        orientation,
      } as InvoiceTemplate['settings'],
    };

    onSave(templateData);
  };

  // Expose save to parent via ref
  useImperativeHandle(ref, () => ({
    save: handleSave,
  }));

  // Notify parent of changes
  useEffect(() => {
    if (onChange) {
      const templateData: Partial<InvoiceTemplate> = {
        id: template?.id,
        name,
        description,
        branding: {
          logo: companyLogo,
          primaryColor, // Task 6.3b
          secondaryColor: template?.branding?.secondaryColor || '#64748b',
          fontFamily, // Task 6.3e
          logoPosition: logoPosition,
          textColor: template?.branding?.textColor || '#1e293b',
          fontSize: template?.branding?.fontSize || 14,
        },
        sections: sections.map((section) => ({
          id: section.id,
          type: section.id === 'lineItems' ? 'line-items' as const : section.id === 'invoice' ? 'header' as const : section.id === 'project' ? 'summary' as const : section.id as 'header' | 'summary' | 'line-items' | 'totals' | 'footer' | 'custom',
          visible: true,
          order: section.order,
          config: getSectionConfig(section.id),
        })) as InvoiceTemplate['sections'],
        settings: {
          ...template?.settings,
          groupBy: groupBy as InvoiceTemplate['settings']['groupBy'],
          orientation,
        } as InvoiceTemplate['settings'],
      };
      onChange(templateData);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, description, companyLogo, logoPosition, sections, groupBy, includeBackingSheet,
      backingSheetOrientation, backingSheetColumnOrder, backingSheetColumns,
      orientation, columnOrder,
      showCompanyName, showCompanyAddress, showCompanyPhone, showCompanyVAT, showCompanyRegistration,
      showClientName, showClientAddress, showClientContact,
      showInvoiceNumber, showInvoiceDate, showDueDate,
      showProjectCode, showProjectName, showProjectReference, showProjectManager,
      showDate, showDescription,
      showCostCentreCode, showCostCentreName,
      showPerson, showRole, showQty, showRate, showVAT, showVATCode, showAmount, showType,
      showSubtotal, showTax, showDiscount, showTotal,
      showBankDetails, showNotes, showTermsConditions, notesContent, termsContent,
      showCompanyLogo,
      primaryColor, fontFamily,
      alternateRowColors, showBorders,
      bsShowLogo, bsShowCompanyName, bsShowCompanyAddress,
      bsShowInvoiceNumber, bsShowInvoiceDate, bsShowClientName, bsShowClientAddress,
      bsShowBankDetails, bsShowNotes, bsShowTermsConditions,
      groupedRowDescription, groupedRowDescriptionExpense, groupedRowDescriptionPurchase]); // Task 6.3b / 6.3e / 6.4b / 6.4d

  const getSectionConfig = (sectionId: SectionId) => {
    switch (sectionId) {
      case 'logo':
        return {
          showLogo: showCompanyLogo,
          logoPosition,
        };
      case 'company':
        return {
          showCompanyName,
          showCompanyAddress,
          showPhone: showCompanyPhone,
          showVAT: showCompanyVAT,
          showRegistration: showCompanyRegistration,
          position: companyPosition,
        };
      case 'client':
        return {
          showClientName,
          showClientAddress,
          showClientContact,
          position: clientPosition,
        };
      case 'invoice':
        return {
          showInvoiceNumber,
          showInvoiceDate,
          showDueDate,
          position: invoiceDetailsPosition,
        };
      case 'project':
        return {
          showProjectCode,
          showProjectName,
          showProjectReference,
          showProjectManager,
          position: projectPosition,
        };
      case 'lineItems':
        return {
          groupBy,
          groupedRowDescription,
          groupedRowDescriptionExpense,
          groupedRowDescriptionPurchase,
          includeBackingSheet,
          backingSheetOrientation,
          backingSheetColumnOrder,
          backingSheetColumns,
          alternateRowColors, // Task 6.4b
          showBorders,        // Task 6.4d
          columnOrder,
          columns: {
            date: showDate,
            description: showDescription,
            costCentreCode: showCostCentreCode,
            costCentreName: showCostCentreName,
            person: showPerson,
            role: showRole,
            qty: showQty,
            rate: showRate,
            vat: showVAT,
            vatCode: showVATCode,
            amount: showAmount,
            type: showType,
          },
          bsShowLogo,
          bsShowCompanyName,
          bsShowCompanyAddress,
          bsShowInvoiceNumber,
          bsShowInvoiceDate,
          bsShowClientName,
          bsShowClientAddress,
          bsShowBankDetails,
          bsShowNotes,
          bsShowTermsConditions,
        };
      case 'totals':
        return {
          showSubtotal,
          showTax,
          showDiscount,
          showTotal,
        };
      case 'footer':
        return {
          showBankDetails,
          showNotes,
          showTermsConditions,
          notesContent,
          termsContent,
          position: footerPosition,
        };
      case 'backingSheet':
        // Backing sheet config is stored inside the lineItems section config.
        // This case exists solely so the SectionId type is satisfied.
        return {};
      default:
        return {};
    }
  };

  const getExpandedState = (sectionId: SectionId) => {
    switch (sectionId) {
      case 'logo': return logoExpanded;
      case 'company': return companyExpanded;
      case 'client': return clientExpanded;
      case 'invoice': return invoiceExpanded;
      case 'project': return projectExpanded;
      case 'lineItems': return lineItemsExpanded;
      case 'totals': return totalsExpanded;
      case 'footer': return footerExpanded;
      case 'backingSheet': return backingSheetExpanded;
      default: return false;
    }
  };

  const setExpandedState = (sectionId: SectionId, expanded: boolean) => {
    switch (sectionId) {
      case 'logo': setLogoExpanded(expanded); break;
      case 'company': setCompanyExpanded(expanded); break;
      case 'client': setClientExpanded(expanded); break;
      case 'invoice': setInvoiceExpanded(expanded); break;
      case 'project': setProjectExpanded(expanded); break;
      case 'lineItems': setLineItemsExpanded(expanded); break;
      case 'totals': setTotalsExpanded(expanded); break;
      case 'footer': setFooterExpanded(expanded); break;
      case 'backingSheet': setBackingSheetExpanded(expanded); break;
    }
  };

  const renderSectionContent = (sectionId: SectionId) => {
    switch (sectionId) {
      case 'logo':
        return (
          <>
            <FieldToggle
              label="Show Company Logo"
              checked={showCompanyLogo}
              onCheckedChange={setShowCompanyLogo}
            />
            {showCompanyLogo && (
              <div className="ml-6 space-y-4 pb-2">
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Upload Logo</Label>
                  <div className="flex items-start gap-3">
                    {companyLogo ? (
                      <div className="flex items-center gap-3">
                        <img
                          src={companyLogo}
                          alt="Company Logo"
                          className="h-16 w-auto max-w-[200px] object-contain border rounded p-2 bg-white"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setCompanyLogo('')}
                        >
                          Remove
                        </Button>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-2 w-full">
                        <input
                          type="file"
                          id="logo-upload"
                          accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              if (file.size > 10 * 1024 * 1024) {
                                toast.error('File size must be less than 10MB');
                                return;
                              }
                              const reader = new FileReader();
                              reader.onloadend = () => {
                                setCompanyLogo(reader.result as string);
                                toast.success('Logo uploaded successfully');
                              };
                              reader.onerror = () => {
                                toast.error('Failed to upload logo');
                              };
                              reader.readAsDataURL(file);
                            }
                            e.target.value = '';
                          }}
                          className="hidden"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => document.getElementById('logo-upload')?.click()}
                          className="w-fit"
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          Upload Logo
                        </Button>
                        <p className="text-xs text-muted-foreground">
                          PNG, JPG, or SVG (max 10MB)
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Logo Position</Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={logoPosition === 'left' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setLogoPosition('left')}
                      className="flex-1"
                    >
                      Left
                    </Button>
                    <Button
                      type="button"
                      variant={logoPosition === 'center' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setLogoPosition('center')}
                      className="flex-1"
                    >
                      Center
                    </Button>
                    <Button
                      type="button"
                      variant={logoPosition === 'right' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setLogoPosition('right')}
                      className="flex-1"
                    >
                      Right
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </>
        );

      case 'company':
        return (
          <div className="space-y-4">
            <div className="space-y-3">
              <FieldToggle
                label="Company Name"
                checked={showCompanyName}
                onCheckedChange={setShowCompanyName}
              />
              <FieldToggle
                label="Company Address"
                checked={showCompanyAddress}
                onCheckedChange={setShowCompanyAddress}
              />
              <FieldToggle
                label="Phone / Email"
                checked={showCompanyPhone}
                onCheckedChange={setShowCompanyPhone}
              />
              <FieldToggle
                label="VAT Registration No."
                checked={showCompanyVAT}
                onCheckedChange={setShowCompanyVAT}
              />
              <FieldToggle
                label="Company Registration No."
                checked={showCompanyRegistration}
                onCheckedChange={setShowCompanyRegistration}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Position</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={companyPosition === 'left' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setCompanyPosition('left')}
                  className="flex-1"
                >
                  Left
                </Button>
                <Button
                  type="button"
                  variant={companyPosition === 'center' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setCompanyPosition('center')}
                  className="flex-1"
                >
                  Center
                </Button>
                <Button
                  type="button"
                  variant={companyPosition === 'right' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setCompanyPosition('right')}
                  className="flex-1"
                >
                  Right
                </Button>
              </div>
            </div>
          </div>
        );

      case 'client':
        return (
          <div className="space-y-4">
            <div className="space-y-3">
              <FieldToggle
                label="Client Name"
                checked={showClientName}
                onCheckedChange={setShowClientName}
              />
              <FieldToggle
                label="Client Address"
                checked={showClientAddress}
                onCheckedChange={setShowClientAddress}
              />
              <FieldToggle
                label="Client Contact Name"
                checked={showClientContact}
                onCheckedChange={setShowClientContact}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Position</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={clientPosition === 'left' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setClientPosition('left')}
                  className="flex-1"
                >
                  Left
                </Button>
                <Button
                  type="button"
                  variant={clientPosition === 'center' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setClientPosition('center')}
                  className="flex-1"
                >
                  Center
                </Button>
                <Button
                  type="button"
                  variant={clientPosition === 'right' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setClientPosition('right')}
                  className="flex-1"
                >
                  Right
                </Button>
              </div>
            </div>
          </div>
        );

      case 'invoice':
        return (
          <div className="space-y-4">
            <div className="space-y-3">
              <FieldToggle
                label="Invoice Number"
                checked={showInvoiceNumber}
                onCheckedChange={setShowInvoiceNumber}
              />
              <FieldToggle
                label="Invoice Date"
                checked={showInvoiceDate}
                onCheckedChange={setShowInvoiceDate}
              />
              <FieldToggle
                label="Due Date"
                checked={showDueDate}
                onCheckedChange={setShowDueDate}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Position</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={invoiceDetailsPosition === 'left' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setInvoiceDetailsPosition('left')}
                  className="flex-1"
                >
                  Left
                </Button>
                <Button
                  type="button"
                  variant={invoiceDetailsPosition === 'center' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setInvoiceDetailsPosition('center')}
                  className="flex-1"
                >
                  Center
                </Button>
                <Button
                  type="button"
                  variant={invoiceDetailsPosition === 'right' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setInvoiceDetailsPosition('right')}
                  className="flex-1"
                >
                  Right
                </Button>
              </div>
            </div>
          </div>
        );

      case 'project':
        return (
          <div className="space-y-4">
            <div className="space-y-3">
              <FieldToggle
                label="Project Code"
                checked={showProjectCode}
                onCheckedChange={setShowProjectCode}
              />
              <FieldToggle
                label="Project Name"
                checked={showProjectName}
                onCheckedChange={setShowProjectName}
              />
              <FieldToggle
                label="Project Description"
                checked={showProjectReference}
                onCheckedChange={setShowProjectReference}
              />
              <FieldToggle
                label="Project Manager"
                checked={showProjectManager}
                onCheckedChange={setShowProjectManager}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Position</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={projectPosition === 'left' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setProjectPosition('left')}
                  className="flex-1"
                >
                  Left
                </Button>
                <Button
                  type="button"
                  variant={projectPosition === 'center' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setProjectPosition('center')}
                  className="flex-1"
                >
                  Center
                </Button>
                <Button
                  type="button"
                  variant={projectPosition === 'right' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setProjectPosition('right')}
                  className="flex-1"
                >
                  Right
                </Button>
              </div>
            </div>
          </div>
        );

      case 'lineItems': {
        // ── Column visibility map for lookup in reorderable list ── Task 2.7c
        const columnVisibility: Record<string, { label: string; checked: boolean; onCheckedChange: (v: boolean) => void }> = {
          date:            { label: 'Date',            checked: showDate,            onCheckedChange: setShowDate },
          description:     { label: 'Description',     checked: showDescription,     onCheckedChange: setShowDescription },
          person:          { label: 'Person',          checked: showPerson,          onCheckedChange: setShowPerson },
          role:            { label: 'Role',            checked: showRole,            onCheckedChange: setShowRole },
          qty:             { label: 'Qty',             checked: showQty,             onCheckedChange: setShowQty },
          net:             { label: 'Rate',            checked: showRate,            onCheckedChange: setShowRate },
          vat:             { label: 'VAT',             checked: showVAT,             onCheckedChange: setShowVAT },
          vatCode:         { label: 'VAT Code',        checked: showVATCode,         onCheckedChange: setShowVATCode },
          amount:          { label: 'Amount',          checked: showAmount,          onCheckedChange: setShowAmount },
          costCentreCode:  { label: 'Cost Centre Code',checked: showCostCentreCode,  onCheckedChange: setShowCostCentreCode },
          costCentreName:  { label: 'Cost Centre Name',checked: showCostCentreName,  onCheckedChange: setShowCostCentreName },
          type:            { label: 'Type',            checked: showType,            onCheckedChange: setShowType },
        };

        // ── Active column count for warning indicator — Task 2.5a ──
        const activeColumnCount = columnOrder.filter(f => columnVisibility[f]?.checked).length;

        // ── Colour thresholds — Task 2.5c / 2.5d ──
        const getIndicatorConfig = () => {
          if (orientation === 'landscape') {
            if (activeColumnCount <= 8) return { color: 'text-success-600 bg-success-50 border-success-200', icon: '●', msg: `${activeColumnCount} columns — fits A4 landscape` };
            if (activeColumnCount <= 10) return { color: 'text-warning-600 bg-warning-50 border-warning-200', icon: '⚠', msg: `${activeColumnCount} columns — tight on landscape, consider reducing` };
            return { color: 'text-error-600 bg-error-50 border-error-200', icon: '✕', msg: `${activeColumnCount} columns — will overflow A4 landscape. Reduce columns.` };
          }
          // Portrait thresholds
          if (activeColumnCount <= 5) return { color: 'text-success-600 bg-success-50 border-success-200', icon: '●', msg: `${activeColumnCount} columns — fits A4 portrait` };
          if (activeColumnCount <= 7) return { color: 'text-warning-600 bg-warning-50 border-warning-200', icon: '⚠', msg: `${activeColumnCount} columns — tight on portrait, consider landscape` };
          return { color: 'text-error-600 bg-error-50 border-error-200', icon: '✕', msg: `${activeColumnCount} columns — will overflow A4 portrait. Switch to landscape or reduce columns.` };
        };
        const indicator = getIndicatorConfig();

        return (
          <div className="space-y-1">
            {/* Group By */}
            <div className="flex items-center justify-between py-1">
              <Label>Group By</Label>
              <Select value={groupBy} onValueChange={setGroupBy}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="person">Person</SelectItem>
                  <SelectItem value="phase">Phase</SelectItem>
                  <SelectItem value="task">Task</SelectItem>
                  <SelectItem value="role">Role</SelectItem>
                  <SelectItem value="cost-center">Cost Center</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* ── Column Count Indicator — Task 2.5b ── */}
            <div className={`flex items-start gap-2 rounded-md border px-3 py-2 text-xs mt-1 mb-2 ${indicator.color}`}>
              <span className="shrink-0 mt-0.5">{indicator.icon}</span>
              <span>{indicator.msg}</span>
            </div>

            {/* ── Column Drag-and-Drop Reorder — Task 2.7 ── */}
            <div className="pt-1 pb-1">
              <p className="text-xs text-muted-foreground mb-2 font-medium">Columns — drag to reorder</p>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={(e) => setActiveColumnDragId(e.active.id as string)}
                onDragEnd={(e) => {
                  const { active, over } = e;
                  setActiveColumnDragId(null);
                  if (over && active.id !== over.id) {
                    setColumnOrder((prev) => {
                      const oldIdx = prev.indexOf(active.id as ColumnFieldType);
                      const newIdx = prev.indexOf(over.id as ColumnFieldType);
                      return arrayMove(prev, oldIdx, newIdx);
                    });
                  }
                }}
              >
                <SortableContext items={columnOrder} strategy={verticalListSortingStrategy}>
                  <div className="space-y-0.5">
                    {columnOrder.map((field) => {
                      const col = columnVisibility[field];
                      if (!col) return null;
                      // Task 2.5e-i: when grouped, only Description and Amount are active
                      const isGroupedOut =
                        groupBy !== 'none' &&
                        field !== 'description' &&
                        field !== 'amount';

                      // Task 3.8 — show Grouped Row Description editor row immediately before the Description toggle
                      const descEditorRow = (() => {
                        if (field !== 'description' || groupBy === 'none') return null;
                        const firstGroup = (() => {
                          const groups = groupLineItems(
                            mockPreviewData.lineItems,
                            groupBy as Parameters<typeof groupLineItems>[1],
                          );
                          return groups && groups.length > 0 ? groups[0] : null;
                        })();
                        // Pick the timesheet template for the sidebar preview (most common; types vary per group)
                        const tpl = getDescriptionTemplateForGroup(
                          firstGroup?.items ?? [],
                          groupedRowDescription,
                          groupedRowDescriptionExpense,
                          groupedRowDescriptionPurchase,
                        );
                        const rawText = tpl.replace(/<[^>]*>/g, '').trim();
                        const resolvedPreview = firstGroup && rawText
                          ? resolveGroupLabel(rawText, firstGroup.items, {
                              totalQty: firstGroup.totalQty,
                              subtotalAmount: firstGroup.subtotalAmount,
                              itemCount: firstGroup.itemCount,
                            })
                          : rawText || '—';
                        return (
                          <div
                            key="grouped-row-description"
                            className="mb-0.5 rounded-md border border-border bg-muted/30 px-3 py-2 flex items-center justify-between gap-2"
                          >
                            <div className="min-w-0">
                              <Label className="text-xs font-medium">Grouped Row Description</Label>
                              <p className="text-xs text-muted-foreground truncate mt-0.5">
                                &ldquo;{resolvedPreview}&rdquo;
                              </p>
                            </div>
                            <button
                              type="button"
                              title="Edit grouped row description"
                              className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
                              onClick={() => {
                                setDescDraftTimesheet(groupedRowDescription);
                                setDescDraftExpense(groupedRowDescriptionExpense);
                                setDescDraftPurchase(groupedRowDescriptionPurchase);
                                setDescActiveTab('timesheet');
                                setDescDialogOpen(true);
                              }}
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        );
                      })();

                      return (
                        <React.Fragment key={field}>
                          {descEditorRow}
                          <SortableColumnRow
                            id={field}
                            label={col.label}
                            checked={col.checked}
                            onCheckedChange={col.onCheckedChange}
                            disabled={isGroupedOut}
                          />
                        </React.Fragment>
                      );
                    })}
                  </div>
                </SortableContext>
                <DragOverlay>
                  {activeColumnDragId ? (
                    <div className="flex items-center gap-2 border rounded bg-background shadow px-2 py-1.5 text-sm opacity-90">
                      <GripVertical className="h-3.5 w-3.5 text-muted-foreground" />
                      <span>{columnVisibility[activeColumnDragId]?.label ?? activeColumnDragId}</span>
                    </div>
                  ) : null}
                </DragOverlay>
              </DndContext>
            </div>

            {/* Task 2.5e-iii — Grouped mode info note: only show when suppressed columns are actually enabled */}
            {groupBy !== 'none' &&
              columnOrder.some(
                f => f !== 'description' && f !== 'amount' && columnVisibility[f]?.checked
              ) && (
              <p className="text-xs text-muted-foreground mt-1 leading-snug">
                Only <span className="font-medium">Description</span> and <span className="font-medium">Amount</span> are shown on grouped summary rows. Other column settings apply to the Backing Sheet when enabled.
              </p>
            )}
          </div>
        );
      }

      case 'totals':
        return (
          <>
            <FieldToggle
              label="Subtotal"
              checked={showSubtotal}
              onCheckedChange={setShowSubtotal}
            />
            <FieldToggle
              label="VAT"
              checked={showTax}
              onCheckedChange={setShowTax}
            />
            <FieldToggle
              label="Discount"
              checked={showDiscount}
              onCheckedChange={setShowDiscount}
            />
            <FieldToggle
              label="Total"
              checked={showTotal}
              onCheckedChange={setShowTotal}
            />
          </>
        );

      case 'footer':
        return (
          <div className="space-y-4">
            <div className="space-y-3">
              <FieldToggle
                label="Bank Details"
                checked={showBankDetails}
                onCheckedChange={setShowBankDetails}
              />
              <div className="flex items-center justify-between py-1">
                <Label htmlFor="notes-toggle" className="cursor-pointer text-sm font-normal">
                  Notes
                </Label>
                <div className="flex items-center gap-2">
                  {showNotes && (
                    <button
                      type="button"
                      title={notesContent ? 'Edit notes' : 'Add notes'}
                      className="text-muted-foreground hover:text-foreground transition-colors"
                      onClick={() => {
                        setNotesDraft(notesContent);
                        setNotesDialogOpen(true);
                      }}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                  )}
                  <Switch
                    id="notes-toggle"
                    checked={showNotes}
                    onCheckedChange={setShowNotes}
                  />
                </div>
              </div>
              <div className="flex items-center justify-between py-1">
                <Label htmlFor="terms-toggle" className="cursor-pointer text-sm font-normal">
                  Terms &amp; Conditions
                </Label>
                <div className="flex items-center gap-2">
                  {showTermsConditions && (
                    <button
                      type="button"
                      title={termsContent ? 'Edit terms & conditions' : 'Add terms & conditions'}
                      className="text-muted-foreground hover:text-foreground transition-colors"
                      onClick={() => {
                        setTermsDraft(termsContent);
                        setTermsDialogOpen(true);
                      }}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                  )}
                  <Switch
                    id="terms-toggle"
                    checked={showTermsConditions}
                    onCheckedChange={setShowTermsConditions}
                  />
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Position</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={footerPosition === 'left' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFooterPosition('left')}
                  className="flex-1"
                >
                  Left
                </Button>
                <Button
                  type="button"
                  variant={footerPosition === 'center' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFooterPosition('center')}
                  className="flex-1"
                >
                  Center
                </Button>
                <Button
                  type="button"
                  variant={footerPosition === 'right' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFooterPosition('right')}
                  className="flex-1"
                >
                  Right
                </Button>
              </div>
            </div>
          </div>
        );

      case 'backingSheet':
        return (
          <BackingSheetPanel
            includeBackingSheet={includeBackingSheet}
            setIncludeBackingSheet={setIncludeBackingSheet}
            backingSheetOrientation={backingSheetOrientation}
            setBackingSheetOrientation={setBackingSheetOrientation}
            backingSheetColumnOrder={backingSheetColumnOrder}
            setBackingSheetColumnOrder={setBackingSheetColumnOrder}
            backingSheetColumns={backingSheetColumns}
            setBackingSheetColumns={setBackingSheetColumns}
            activeBsColumnDragId={activeBsColumnDragId}
            setActiveBsColumnDragId={setActiveBsColumnDragId}
            sensors={sensors}
            bsShowLogo={bsShowLogo} setBsShowLogo={setBsShowLogo}
            bsShowCompanyName={bsShowCompanyName} setBsShowCompanyName={setBsShowCompanyName}
            bsShowCompanyAddress={bsShowCompanyAddress} setBsShowCompanyAddress={setBsShowCompanyAddress}
            bsShowInvoiceNumber={bsShowInvoiceNumber} setBsShowInvoiceNumber={setBsShowInvoiceNumber}
            bsShowInvoiceDate={bsShowInvoiceDate} setBsShowInvoiceDate={setBsShowInvoiceDate}
            bsShowClientName={bsShowClientName} setBsShowClientName={setBsShowClientName}
            bsShowClientAddress={bsShowClientAddress} setBsShowClientAddress={setBsShowClientAddress}
            bsShowBankDetails={bsShowBankDetails} setBsShowBankDetails={setBsShowBankDetails}
            bsShowNotes={bsShowNotes} setBsShowNotes={setBsShowNotes}
            bsShowTermsConditions={bsShowTermsConditions} setBsShowTermsConditions={setBsShowTermsConditions}
          />
        );

      default:
        return null;
    }
  };

  const renderPreviewSection = (sectionId: SectionId) => {
    switch (sectionId) {
      case 'logo':
        return showCompanyLogo && (
          <div className={`flex ${logoPosition === 'left' ? 'justify-start' : logoPosition === 'center' ? 'justify-center' : 'justify-end'}`}>
            <div className="h-20 w-auto max-w-[250px] rounded flex items-center justify-center">
              {companyLogo ? (
                <img 
                  src={companyLogo} 
                  alt="Company Logo" 
                  className="h-full w-auto max-w-[250px] object-contain"
                />
              ) : (
                <div className="h-20 w-48 bg-muted rounded flex items-center justify-center text-sm text-muted-foreground">
                  Company Logo
                </div>
              )}
            </div>
          </div>
        );

      case 'company':
        return (showCompanyName || showCompanyAddress || showCompanyPhone || showCompanyVAT || showCompanyRegistration) && (
          <div className={`flex ${companyPosition === 'left' ? 'justify-start' : companyPosition === 'center' ? 'justify-center' : 'justify-end'}`}>
            <div className={`space-y-1 ${companyPosition === 'center' ? 'text-center' : companyPosition === 'right' ? 'text-right' : ''}`}>
              {showCompanyName && (
                <div className="font-semibold">{currentCompany?.name || 'Your Company Name'}</div>
              )}
              {showCompanyAddress && currentCompany?.registeredAddress && (
                <div className="text-sm text-muted-foreground whitespace-pre-line">
                  {[
                    currentCompany.registeredAddress.line1,
                    currentCompany.registeredAddress.line2,
                    currentCompany.registeredAddress.city,
                    currentCompany.registeredAddress.county,
                    currentCompany.registeredAddress.postcode,
                    currentCompany.registeredAddress.country,
                  ].filter(Boolean).join('\n')}
                </div>
              )}
              {showCompanyAddress && !currentCompany?.registeredAddress && (
                <div className="text-sm text-muted-foreground">Company address not set</div>
              )}
              {showCompanyPhone && (currentCompany?.phone || currentCompany?.email) && (
                <div className="text-sm text-muted-foreground">
                  {[currentCompany?.phone, currentCompany?.email].filter(Boolean).join(' | ')}
                </div>
              )}
              {showCompanyVAT && currentCompany?.vatNumber && (
                <div className="text-sm text-muted-foreground">
                  VAT Reg: {currentCompany.vatNumber}
                </div>
              )}
              {showCompanyRegistration && currentCompany?.companyRegistrationNumber && (
                <div className="text-sm text-muted-foreground">
                  Company Reg: {currentCompany.companyRegistrationNumber}
                </div>
              )}
            </div>
          </div>
        );

      case 'client':
        return (showClientName || showClientAddress || showClientContact) && (
          <div className={`flex ${clientPosition === 'left' ? 'justify-start' : clientPosition === 'center' ? 'justify-center' : 'justify-end'}`}>
            <div className={`space-y-1 ${clientPosition === 'center' ? 'text-center' : clientPosition === 'right' ? 'text-right' : ''}`}>
              <div className="text-sm font-medium text-muted-foreground">Bill To:</div>
              {showClientName && <div className="font-medium">Client Name</div>}
              {showClientAddress && (
                <div className="text-sm text-muted-foreground whitespace-pre-line">
                  {'456 Client Avenue\nClient City\nCountry\nAB1 2CD'}
                </div>
              )}
              {showClientContact && (
                <div className="text-sm text-muted-foreground">
                  Contact: John Doe
                </div>
              )}
            </div>
          </div>
        );

      case 'invoice':
        return (showInvoiceNumber || showInvoiceDate || showDueDate) && (
          <div className={`flex ${invoiceDetailsPosition === 'left' ? 'justify-start' : invoiceDetailsPosition === 'center' ? 'justify-center' : 'justify-end'}`}>
            <div className={`space-y-1 ${invoiceDetailsPosition === 'center' ? 'text-center' : invoiceDetailsPosition === 'right' ? 'text-right' : ''}`}>
              {showInvoiceNumber && (
                <div className="text-2xl font-bold">INV-001</div>
              )}
              {showInvoiceDate && (
                <div className="text-sm text-muted-foreground">
                  Date: {new Date().toLocaleDateString()}
                </div>
              )}
              {showDueDate && (
                <div className="text-sm text-muted-foreground">
                  Due: {new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString()}
                </div>
              )}
            </div>
          </div>
        );

      case 'project':
        return (showProjectCode || showProjectName || showProjectReference || showProjectManager) && (
          <div className={`flex ${projectPosition === 'left' ? 'justify-start' : projectPosition === 'center' ? 'justify-center' : 'justify-end'}`}>
            <div className={`space-y-1 ${projectPosition === 'center' ? 'text-center' : projectPosition === 'right' ? 'text-right' : ''}`}>
              {(showProjectCode || showProjectName) && (
                <div>
                  <span className="text-sm text-muted-foreground">Project: </span>
                  <span className="font-medium">
                    {showProjectCode && showProjectName && "PRJ-2024-001 - Website Redesign Project"}
                    {showProjectCode && !showProjectName && "PRJ-2024-001"}
                    {!showProjectCode && showProjectName && "Website Redesign Project"}
                  </span>
                </div>
              )}
              {showProjectReference && (
                <div>
                  <span className="text-sm text-muted-foreground">Description: </span>
                  <span>Internal website redesign and modernization</span>
                </div>
              )}
              {showProjectManager && (
                <div>
                  <span className="text-sm text-muted-foreground">Project Manager: </span>
                  <span>John Smith</span>
                </div>
              )}
            </div>
          </div>
        );

      case 'lineItems': {
        // ── Column visibility & rendering config for ordered preview — Task 2.8 ──
        const previewColDefs: Record<string, {
          show: boolean;
          label: string;
          align: 'left' | 'right';
          renderCell: (item: typeof mockPreviewData.lineItems[number]) => React.ReactNode;
          summaryCell: (group: ReturnType<typeof groupLineItems> extends (infer G)[] | null ? G : never) => React.ReactNode;
        }> = {
          date: {
            show: showDate, label: 'Date', align: 'left',
            renderCell: (item) => item.date,
            summaryCell: () => '',
          },
          description: {
            show: showDescription, label: 'Description', align: 'left',
            renderCell: (item) => item.description,
            summaryCell: (group) => {
              // Task 3.8f — pick the correct description template by group type, resolve tokens
              const tpl = getDescriptionTemplateForGroup(
                group.items,
                groupedRowDescription,
                groupedRowDescriptionExpense,
                groupedRowDescriptionPurchase,
              );
              const rawDesc = tpl.replace(/<[^>]*>/g, '').trim();
              const resolved = rawDesc
                ? resolveGroupLabel(rawDesc, group.items, {
                    totalQty: group.totalQty,
                    subtotalAmount: group.subtotalAmount,
                    itemCount: group.itemCount,
                  })
                : group.groupLabel;
              return <span className="font-medium">{resolved}</span>;
            },
          },
          person: {
            show: showPerson, label: 'Person', align: 'left',
            renderCell: (item) => item.person || '-',
            summaryCell: () => '',
          },
          role: {
            show: showRole, label: 'Role', align: 'left',
            renderCell: (item) => item.role || '-',
            summaryCell: () => '',
          },
          qty: {
            show: showQty, label: 'Qty', align: 'right',
            renderCell: (item) => item.qty.toFixed(1),
            summaryCell: () => '',
          },
          net: {
            show: showRate, label: 'Rate', align: 'right',
            renderCell: (item) => `£${item.net.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            summaryCell: () => '',
          },
          vat: {
            show: showVAT, label: 'VAT', align: 'right',
            renderCell: (item) => `£${item.vat.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            summaryCell: () => '',
          },
          vatCode: {
            show: showVATCode, label: 'VAT Code', align: 'left',
            renderCell: (item) => item.vatCode,
            summaryCell: () => '',
          },
          amount: {
            show: showAmount, label: 'Amount', align: 'right',
            renderCell: (item) => `£${item.amount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            summaryCell: (group) => (
              <span className="font-medium">
                £{group.subtotalAmount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            ),
          },
          costCentreCode: {
            show: showCostCentreCode, label: 'Cost Centre Code', align: 'left',
            renderCell: (item) => item.costCentreCode,
            summaryCell: () => '',
          },
          costCentreName: {
            show: showCostCentreName, label: 'Cost Centre Name', align: 'left',
            renderCell: (item) => item.costCentreName,
            summaryCell: () => '',
          },
          type: {
            show: showType, label: 'Type', align: 'left',
            renderCell: (item) => {
              const t = item.lineItemType ?? 'timesheet';
              return t === 'timesheet' ? 'Timesheet' : t === 'expense' ? 'Expense' : 'Purchase';
            },
            summaryCell: () => '',
          },
        };

        // Render columns in columnOrder sequence (Task 2.8)
        const isGroupedOut = (f: string) =>
          groupBy !== 'none' && f !== 'description' && f !== 'amount';

        const visibleColOrder = columnOrder.filter(f => previewColDefs[f]?.show && !isGroupedOut(f));

        const grouped = groupLineItems(
          mockPreviewData.lineItems,
          groupBy as Parameters<typeof groupLineItems>[1],
        );

        // Task 6.5b — accent colour for table header
        const accentBg = primaryColor || '#2563eb';
        const accentText = getContrastTextColor(accentBg);
        // Task 6.7b — 5% accent tint for alternate rows (transparent when toggle is off)
        const accentRowBg = alternateRowColors ? hexToRgba(accentBg, 0.05) : 'transparent';
        return (
          <div className="border rounded-lg overflow-hidden mt-4">
            <table className={`w-full text-sm table-fixed ${showBorders ? 'border-collapse' : ''}`}>
              <thead style={{ backgroundColor: accentBg, color: accentText }}>
                <tr>
                  {visibleColOrder.map(field => {
                    const col = previewColDefs[field];
                    return (
                      <th key={field} className={`p-2 whitespace-normal break-words ${col.align === 'right' ? 'text-right' : 'text-left'} ${showBorders ? 'border border-border' : ''}`}>
                        {col.label}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {grouped === null
                  ? mockPreviewData.lineItems.map((item, idx) => (
                      <tr key={item.id} className={showBorders ? '' : 'border-t'} style={{ backgroundColor: idx % 2 !== 0 ? accentRowBg : undefined }}>
                        {visibleColOrder.map(field => {
                          const col = previewColDefs[field];
                          return (
                            <td key={field} className={`p-2 ${field === 'date' ? 'whitespace-nowrap' : ''} ${col.align === 'right' ? 'text-right' : ''} ${showBorders ? 'border border-border' : ''}`}>
                              {col.renderCell(item)}
                            </td>
                          );
                        })}
                      </tr>
                    ))
                  : grouped.map((group, idx) => (
                      <tr key={`group-summary-${group.groupKey}`} className={showBorders ? '' : 'border-t'} style={{ backgroundColor: idx % 2 !== 0 ? accentRowBg : undefined }}>
                        {visibleColOrder.map(field => {
                          const col = previewColDefs[field];
                          return (
                            <td key={field} className={`p-2 ${col.align === 'right' ? 'text-right' : ''} ${showBorders ? 'border border-border' : ''}`}>
                              {col.summaryCell(group)}
                            </td>
                          );
                        })}
                      </tr>
                    ))
                }
              </tbody>
            </table>
          </div>
        );
      }

      case 'totals':
        return (
          <div className="flex justify-end">
            <div className="w-64 space-y-2">
              {showSubtotal && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal:</span>
                  <span>£{mockPreviewData.totals.subtotal.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
              )}
              {showTax && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">VAT (20%):</span>
                  <span>£{mockPreviewData.totals.tax.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
              )}
              {showDiscount && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Discount:</span>
                  <span>-£{mockPreviewData.totals.discount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
              )}
              {showTotal && (
                <div className="flex justify-between font-bold text-lg border-t pt-2" style={{ borderColor: primaryColor || '#2563eb' }}>
                  <span style={{ color: primaryColor || '#2563eb' }}>Total:</span>
                  <span>£{mockPreviewData.totals.total.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
              )}
            </div>
          </div>
        );

      case 'footer':
        return (showBankDetails || showNotes || showTermsConditions) && (
          <div className={`${showNotes ? '' : 'flex'} ${!showNotes && footerPosition === 'left' ? 'justify-start' : !showNotes && footerPosition === 'center' ? 'justify-center' : !showNotes && footerPosition === 'right' ? 'justify-end' : ''} border-t pt-4`}>
            <div className={`space-y-3 text-sm text-muted-foreground ${!showNotes && footerPosition === 'center' ? 'text-center' : !showNotes && footerPosition === 'right' ? 'text-right' : ''} ${showNotes ? 'w-full' : ''}`}>
              {showBankDetails && (
                <div>
                  <div className="font-medium text-foreground">Bank Details:</div>
                  {currentCompany?.bankDetails ? (
                    <div className="space-y-0.5">
                      {currentCompany.bankDetails.bankName && <div>{currentCompany.bankDetails.bankName}</div>}
                      {currentCompany.bankDetails.accountName && <div>Account Name: {currentCompany.bankDetails.accountName}</div>}
                      {currentCompany.bankDetails.sortCode && <div>Sort Code: {currentCompany.bankDetails.sortCode}</div>}
                      {currentCompany.bankDetails.accountNumber && <div>Account No: {currentCompany.bankDetails.accountNumber}</div>}
                      {currentCompany.bankDetails.iban && <div>IBAN: {currentCompany.bankDetails.iban}</div>}
                      {currentCompany.bankDetails.swiftBic && <div>SWIFT/BIC: {currentCompany.bankDetails.swiftBic}</div>}
                    </div>
                  ) : (
                    <div className="text-muted-foreground/60 italic">No bank details set — add them in Settings → General</div>
                  )}
                </div>
              )}
              {showNotes && (
                <div className="w-full">
                  <div className="font-medium text-foreground mb-1">Notes:</div>
                  {migrateNotes(notesContent)
                    ? <div className="prose prose-sm max-w-none text-muted-foreground" dangerouslySetInnerHTML={{ __html: migrateNotes(notesContent) }} />
                    : <span className="text-muted-foreground/50 italic">No notes</span>
                  }
                </div>
              )}
              {showTermsConditions && (
                <div>
                  <div className="font-medium text-foreground">Terms &amp; Conditions:</div>
                  {termsContent
                    ? <div className="prose prose-sm max-w-none text-muted-foreground" dangerouslySetInnerHTML={{ __html: termsContent }} />
                    : <span className="text-muted-foreground/50 italic">No terms &amp; conditions</span>
                  }
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const [detailsVisible, setDetailsVisible] = useState(true);

  // ── Backing Sheet pre-computed node — Task 5.3 ──────────────────────────────
  // (BsPreviewItem and BsColDef are declared at module level above this component)
  const bsColDefs: BsColDef[] = [
    { field: 'date',           label: 'Date',             align: 'left',  renderCell: (i) => i.date },
    { field: 'description',    label: 'Description',      align: 'left',  renderCell: (i) => i.description },
    { field: 'person',         label: 'Person',           align: 'left',  renderCell: (i) => i.person || '-' },
    { field: 'role',           label: 'Role',             align: 'left',  renderCell: (i) => i.role || '-' },
    { field: 'costCentreCode', label: 'Cost Centre Code', align: 'left',  renderCell: (i) => i.costCentreCode },
    { field: 'costCentreName', label: 'Cost Centre Name', align: 'left',  renderCell: (i) => i.costCentreName },
    { field: 'qty',            label: 'Qty',              align: 'right', renderCell: (i) => i.qty.toFixed(1) },
    { field: 'net',            label: 'Rate',             align: 'right', renderCell: (i) => `£${i.net.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` },
    { field: 'vat',            label: 'VAT',              align: 'right', renderCell: (i) => `£${i.vat.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` },
    { field: 'vatCode',        label: 'VAT Code',         align: 'left',  renderCell: (i) => i.vatCode },
    { field: 'amount',         label: 'Amount',           align: 'right', renderCell: (i) => `£${i.amount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` },
    { field: 'type',           label: 'Type',             align: 'left',  renderCell: (i) => {
      const t = i.lineItemType ?? 'timesheet';
      return t === 'timesheet' ? 'Timesheet' : t === 'expense' ? 'Expense' : 'Purchase';
    }},
  ];
  // Task 5.8 — use independent backingSheetColumns visibility; fall back to all-visible when empty
  const bsColVisible = (field: string): boolean => {
    if (Object.keys(backingSheetColumns).length === 0) return true;
    return backingSheetColumns[field as ColumnField] !== false;
  };
  // Task 5.8 — use backingSheetColumnOrder (independent from main invoice column order)
  const visibleBsCols = backingSheetColumnOrder
    .map(f => bsColDefs.find(c => c.field === f))
    .filter((c): c is BsColDef => !!c && bsColVisible(c.field));

  const builderBackingSheetNode = includeBackingSheet ? (
    <>
      {/* Page break divider */}
      <div className="flex items-center gap-3 mt-6 mb-6">
        <div className="flex-1 border-t border-dashed border-muted-foreground/40" />
        <span className="text-xs text-muted-foreground/60 font-medium tracking-wide uppercase whitespace-nowrap">
          — Backing Sheet —
        </span>
        <div className="flex-1 border-t border-dashed border-muted-foreground/40" />
      </div>

      {/* Backing Sheet A4 page — uses backingSheetOrientation, independent of main invoice */}
      <div className="rounded-lg border bg-white shadow-lg" style={{
        width: backingSheetOrientation === 'landscape' ? '297mm' : '210mm',
        minHeight: backingSheetOrientation === 'landscape' ? '210mm' : '297mm',
        padding: '20mm',
        fontFamily: FONT_STACKS[fontFamily] ?? fontFamily,
      }}>
        <div className="space-y-4">
          {/* Independent 3-column header grid */}
          <div className="grid grid-cols-3 gap-4 items-start">
            {/* Left: Logo + Company */}
            <div className="space-y-1 min-w-0">
              {bsShowLogo && (
                <div className="h-12 w-auto max-w-[120px] bg-muted rounded flex items-center justify-center text-xs text-muted-foreground mb-1">Logo</div>
              )}
              {bsShowCompanyName && (
                <div className="font-semibold text-sm">{currentCompany?.name || 'Your Company Name'}</div>
              )}
              {bsShowCompanyAddress && currentCompany?.registeredAddress && (
                <div className="text-xs text-muted-foreground whitespace-pre-line">
                  {[currentCompany.registeredAddress.line1, currentCompany.registeredAddress.line2, currentCompany.registeredAddress.city, currentCompany.registeredAddress.postcode].filter(Boolean).join('\n')}
                </div>
              )}
            </div>
            {/* Centre: Backing Sheet label + Invoice ref */}
            <div className="text-center space-y-1 min-w-0">
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Backing Sheet</div>
              {bsShowInvoiceNumber && <div className="font-bold text-sm">INV-001</div>}
              {bsShowInvoiceDate && <div className="text-xs text-muted-foreground">Date: {new Date().toLocaleDateString()}</div>}
            </div>
            {/* Right: Client */}
            <div className="text-right space-y-1 min-w-0">
              {(bsShowClientName || bsShowClientAddress) && (
                <div className="text-xs text-muted-foreground font-medium">Bill To:</div>
              )}
              {bsShowClientName && <div className="font-medium text-sm">Client Name</div>}
              {bsShowClientAddress && (
                <div className="text-xs text-muted-foreground whitespace-pre-line">{'456 Client Avenue\nClient City\nAB1 2CD'}</div>
              )}
            </div>
          </div>

          {/* Flat detail table — all visible columns, no isGroupedOut suppression */}
          <div className="border rounded-lg overflow-hidden mt-4">
            <table className="w-full text-sm table-fixed">
              {/* Task 6.5b — accent colour on backing sheet thead */}
              <thead style={{ backgroundColor: primaryColor || '#2563eb', color: getContrastTextColor(primaryColor || '#2563eb') }}>
                <tr>
                  {visibleBsCols.map(col => (
                    <th key={col.field} className={`p-2 whitespace-normal break-words ${col.align === 'right' ? 'text-right' : 'text-left'}`}>
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {mockPreviewData.lineItems.map((item, idx) => (
                  <tr key={item.id} className={showBorders ? '' : 'border-t'} style={{ backgroundColor: idx % 2 !== 0 && alternateRowColors ? hexToRgba(primaryColor || '#2563eb', 0.05) : undefined }}>
                    {visibleBsCols.map(col => (
                      <td key={col.field} className={`p-2 ${col.align === 'right' ? 'text-right' : ''} ${showBorders ? 'border border-border' : ''}`}>
                        {col.renderCell(item)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Independent footer */}
          {(bsShowBankDetails || bsShowNotes || bsShowTermsConditions) && (
            <div className="border-t pt-4 space-y-3 text-sm text-muted-foreground">
              {bsShowBankDetails && (
                <div>
                  <div className="font-medium text-foreground text-xs">Bank Details:</div>
                  {currentCompany?.bankDetails ? (
                    <div className="text-xs space-y-0.5">
                      {currentCompany.bankDetails.bankName && <div>{currentCompany.bankDetails.bankName}</div>}
                      {currentCompany.bankDetails.sortCode && <div>Sort Code: {currentCompany.bankDetails.sortCode}</div>}
                      {currentCompany.bankDetails.accountNumber && <div>Account No: {currentCompany.bankDetails.accountNumber}</div>}
                    </div>
                  ) : (
                    <div className="text-xs italic text-muted-foreground/60">No bank details set</div>
                  )}
                </div>
              )}
              {bsShowNotes && notesContent && (
                <div>
                  <div className="font-medium text-foreground text-xs">Notes:</div>
                  <div className="prose prose-xs max-w-none text-muted-foreground text-xs" dangerouslySetInnerHTML={{ __html: migrateNotes(notesContent) }} />
                </div>
              )}
              {bsShowTermsConditions && termsContent && (
                <div>
                  <div className="font-medium text-foreground text-xs">Terms &amp; Conditions:</div>
                  <div className="prose prose-xs max-w-none text-muted-foreground text-xs" dangerouslySetInnerHTML={{ __html: termsContent }} />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  ) : null;
  // ────────────────────────────────────────────────────────────────────────────

  return (
    <div className="h-full bg-background">
      {/* Main Content */}
      <div className={`grid h-full min-h-0 ${detailsVisible ? 'grid-cols-[400px_1fr]' : 'grid-cols-1'}`}>
        {/* Left Panel - Configuration */}
        {detailsVisible && (
        <div className="flex flex-col border-r min-h-0 overflow-hidden">
          {/* Template Name + Description — always visible above tabs (6.1b) */}
          <div className="px-6 pt-6 pb-4 space-y-4 shrink-0">
            <div className="space-y-2">
              <Label>Template Name</Label>
              <Input
                placeholder="e.g., Detailed Invoice, Summary Invoice"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="text-lg font-semibold"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                placeholder="Brief description of this template"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
              />
            </div>
          </div>

          {/* Content / Appearance Tabs — Task 6.1a (6.1e: default "content") */}
          <Tabs defaultValue="content" className="flex flex-col flex-1 min-h-0">
            <div className="px-6 pb-2 shrink-0">
              <TabsList className="w-full">
                <TabsTrigger value="content" className="flex-1">Content</TabsTrigger>
                <TabsTrigger value="appearance" className="flex-1">Appearance</TabsTrigger>
              </TabsList>
            </div>

            {/* ── CONTENT TAB — 6.1b ── */}
            <TabsContent value="content" className="flex-1 overflow-y-auto px-6 pb-6 mt-0 space-y-6">
              <Separator />

              {/* Section Ordering Info */}
              <div className="rounded-lg bg-muted/50 p-4 space-y-2">
                <h4 className="text-sm font-medium">Section Ordering</h4>
                <p className="text-xs text-muted-foreground">
                  Drag sections to reorder them, or use the arrow buttons for keyboard navigation.
                  The preview updates in real-time.
                </p>
              </div>

              {/* Draggable Sections */}
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={sections.map((s) => s.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-3">
                    {sections.map((section, index) => (
                      <SortableSection
                        key={section.id}
                        section={section}
                        expanded={getExpandedState(section.id)}
                        onToggle={() => setExpandedState(section.id, !getExpandedState(section.id))}
                        onMoveUp={() => moveSectionUp(section.id)}
                        onMoveDown={() => moveSectionDown(section.id)}
                        isFirst={index === 0}
                        isLast={index === sections.length - 1}
                      >
                        {renderSectionContent(section.id)}
                      </SortableSection>
                    ))}
                  </div>
                </SortableContext>
                <DragOverlay>
                  {activeDragId ? (
                    <div className="border rounded-lg bg-background shadow-lg opacity-90">
                      <div className="flex items-center gap-2 p-4">
                        <GripVertical className="h-4 w-4 text-muted-foreground" />
                        <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                          {sections.find((s) => s.id === activeDragId)?.title}
                        </h3>
                      </div>
                    </div>
                  ) : null}
                </DragOverlay>
              </DndContext>

              {/* ── Second Page / Backing Sheet — pinned below draggable list (6.1b) ── */}
              <div className="mt-2">
                <div className="flex items-center gap-3 mb-2">
                  <div className="flex-1 h-px bg-border" />
                  <span className="text-xs text-muted-foreground uppercase tracking-wide font-medium shrink-0">
                    Second Page
                  </span>
                  <div className="flex-1 h-px bg-border" />
                </div>
                <div className="border rounded-lg">
                  <div className="flex items-center gap-2 p-4 hover:bg-muted/50 transition-colors">
                    <div className="w-6" />
                    <button
                      type="button"
                      onClick={() => setBackingSheetExpanded(!backingSheetExpanded)}
                      className="flex-1 flex items-center justify-between text-left"
                    >
                      <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                        Backing Sheet
                      </h3>
                      {backingSheetExpanded ? (
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      )}
                    </button>
                  </div>
                  {backingSheetExpanded && (
                    <div className="px-4 pb-4 border-t">
                      <div className="pt-3">
                        {renderSectionContent('backingSheet')}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* ── APPEARANCE TAB — 6.1c ── */}
            <TabsContent value="appearance" className="flex-1 overflow-y-auto px-6 pb-6 mt-0 space-y-6">
              <Separator />

              {/* ── PAGE LAYOUT — moved from Content tab (6.1c / 6.1d) ── */}
              <div className="space-y-4">
                <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Page Layout</h4>

                {/* Page Size (display only — A4 fixed) */}
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Page Size</Label>
                  <div className="h-9 px-3 border rounded-md bg-muted/30 flex items-center text-sm text-muted-foreground">
                    A4
                  </div>
                </div>

                {/* Orientation toggle */}
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Orientation</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setOrientation('portrait')}
                      className={`flex flex-col items-center gap-1.5 rounded-lg border-2 p-3 text-sm transition-colors ${
                        orientation === 'portrait'
                          ? 'border-primary bg-primary/5 text-primary'
                          : 'border-muted hover:border-muted-foreground/30'
                      }`}
                    >
                      <div
                        className={`w-7 h-9 rounded border-2 ${
                          orientation === 'portrait' ? 'border-primary bg-primary/10' : 'border-muted-foreground/40 bg-muted/30'
                        }`}
                      />
                      <span className="font-medium">Portrait</span>
                      <span className="text-xs text-muted-foreground">210 × 297mm</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setOrientation('landscape')}
                      className={`flex flex-col items-center gap-1.5 rounded-lg border-2 p-3 text-sm transition-colors ${
                        orientation === 'landscape'
                          ? 'border-primary bg-primary/5 text-primary'
                          : 'border-muted hover:border-muted-foreground/30'
                      }`}
                    >
                      <div
                        className={`w-9 h-7 rounded border-2 ${
                          orientation === 'landscape' ? 'border-primary bg-primary/10' : 'border-muted-foreground/40 bg-muted/30'
                        }`}
                      />
                      <span className="font-medium">Landscape</span>
                      <span className="text-xs text-muted-foreground">297 × 210mm</span>
                    </button>
                  </div>
                  {orientation === 'landscape' && (
                    <p className="text-xs text-muted-foreground bg-muted/40 rounded-md px-3 py-2">
                      ℹ Landscape adds ~330px of usable width. Suitable when 7+ columns are needed.
                    </p>
                  )}
                </div>
              </div>

              <Separator />

              {/* ── ACCENT COLOUR — Task 6.3a / 6.3b / 6.3c ── */}
              <div className="space-y-4">
                <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Accent Colour</h4>
                <p className="text-xs text-muted-foreground">
                  Applies to the table header background, totals label and header divider line.
                </p>
                <div className="flex items-center gap-3">
                  {/* Colour swatch input — 6.3a */}
                  <div className="relative">
                    <input
                      type="color"
                      value={primaryColor}
                      onChange={e => setPrimaryColor(e.target.value)}
                      className="w-8 h-8 rounded cursor-pointer border border-border p-0.5 bg-transparent"
                      title="Pick accent colour"
                    />
                  </div>
                  {/* Hex label */}
                  <Input
                    value={primaryColor}
                    onChange={e => {
                      const v = e.target.value;
                      if (/^#[0-9a-fA-F]{0,6}$/.test(v)) setPrimaryColor(v);
                    }}
                    className="h-8 w-28 font-mono text-xs"
                    maxLength={7}
                    placeholder="#2563eb"
                  />
                  {/* Reset to default — 6.3c */}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 text-xs text-muted-foreground hover:text-foreground"
                    onClick={() => setPrimaryColor('#2563eb')}
                    disabled={primaryColor === '#2563eb'}
                  >
                    Reset
                  </Button>
                </div>
              </div>

              <Separator />

              {/* ── FONT FAMILY — Task 6.3d / 6.3e ── */}
              <div className="space-y-4">
                <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Font</h4>
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Font Family</Label>
                  <Select
                    value={fontFamily}
                    onValueChange={setFontFamily}
                  >
                    <SelectTrigger className="h-9 text-sm">
                      <SelectValue placeholder="Select font…" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectLabel>Sans-serif</SelectLabel>
                        <SelectItem value="inter"><span style={{ fontFamily: FONT_STACKS.inter }}>Inter</span><span className="ml-2 text-xs text-muted-foreground">(default)</span></SelectItem>
                        <SelectItem value="roboto"><span style={{ fontFamily: FONT_STACKS.roboto }}>Roboto</span></SelectItem>
                        <SelectItem value="helveticaNeue"><span style={{ fontFamily: FONT_STACKS.helveticaNeue }}>Helvetica Neue</span></SelectItem>
                        <SelectItem value="openSans"><span style={{ fontFamily: FONT_STACKS.openSans }}>Open Sans</span></SelectItem>
                        <SelectItem value="lato"><span style={{ fontFamily: FONT_STACKS.lato }}>Lato</span></SelectItem>
                        <SelectItem value="montserrat"><span style={{ fontFamily: FONT_STACKS.montserrat }}>Montserrat</span></SelectItem>
                        <SelectItem value="raleway"><span style={{ fontFamily: FONT_STACKS.raleway }}>Raleway</span></SelectItem>
                        <SelectItem value="nunito"><span style={{ fontFamily: FONT_STACKS.nunito }}>Nunito</span></SelectItem>
                        <SelectItem value="poppins"><span style={{ fontFamily: FONT_STACKS.poppins }}>Poppins</span></SelectItem>
                        <SelectItem value="sourceSans3"><span style={{ fontFamily: FONT_STACKS.sourceSans3 }}>Source Sans 3</span></SelectItem>
                      </SelectGroup>
                      <SelectGroup>
                        <SelectLabel>Serif</SelectLabel>
                        <SelectItem value="georgia"><span style={{ fontFamily: FONT_STACKS.georgia }}>Georgia</span></SelectItem>
                        <SelectItem value="merriweather"><span style={{ fontFamily: FONT_STACKS.merriweather }}>Merriweather</span></SelectItem>
                        <SelectItem value="playfairDisplay"><span style={{ fontFamily: FONT_STACKS.playfairDisplay }}>Playfair Display</span></SelectItem>
                        <SelectItem value="lora"><span style={{ fontFamily: FONT_STACKS.lora }}>Lora</span></SelectItem>
                        <SelectItem value="cormorant"><span style={{ fontFamily: FONT_STACKS.cormorant }}>Cormorant Garamond</span></SelectItem>
                      </SelectGroup>
                      <SelectGroup>
                        <SelectLabel>Monospace</SelectLabel>
                        <SelectItem value="sourceCodePro"><span style={{ fontFamily: FONT_STACKS.sourceCodePro }}>Source Code Pro</span></SelectItem>
                        <SelectItem value="jetbrainsMono"><span style={{ fontFamily: FONT_STACKS.jetbrainsMono }}>JetBrains Mono</span></SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  {/* Live preview — 6.3d */}
                  <p
                    className="text-xs text-muted-foreground bg-muted/40 rounded-md px-3 py-2"
                    style={{ fontFamily: FONT_STACKS[fontFamily] ?? fontFamily }}
                  >
                    The quick brown fox jumps over the lazy dog.
                  </p>
                </div>
              </div>

              <Separator />

              {/* ── TABLE STYLE — Task 6.4a / 6.4b / 6.4c / 6.4d ── */}
              <div className="space-y-4">
                <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Table Style</h4>

                {/* Alternate Row Shading — 6.4a / 6.4b */}
                <div className="flex items-start justify-between gap-4">
                  <div className="space-y-1">
                    <Label className="text-sm font-medium leading-none">Alternate Row Shading</Label>
                    <p className="text-xs text-muted-foreground">
                      Lightly tints every other row using the accent colour.
                    </p>
                  </div>
                  <Switch
                    checked={alternateRowColors}
                    onCheckedChange={setAlternateRowColors}
                    aria-label="Toggle alternate row shading"
                  />
                </div>

                {/* Table Borders — 6.4c / 6.4d */}
                <div className="flex items-start justify-between gap-4">
                  <div className="space-y-1">
                    <Label className="text-sm font-medium leading-none">Table Borders</Label>
                    <p className="text-xs text-muted-foreground">
                      Show grid lines around columns and cells. Off = horizontal dividers only.
                    </p>
                  </div>
                  <Switch
                    checked={showBorders}
                    onCheckedChange={setShowBorders}
                    aria-label="Toggle table borders"
                  />
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
        )}

        {/* Right Panel - Preview */}
        <div className="bg-muted/30 p-6 overflow-y-auto min-h-0">
          <div className="mx-auto" style={{ width: orientation === 'landscape' ? '297mm' : '210mm' }}>
            <div className="mb-4 flex items-center gap-4">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setDetailsVisible(!detailsVisible)}
              >
                {detailsVisible ? (
                  <React.Fragment><PanelLeftClose className="h-4 w-4 mr-1.5" />Hide Details</React.Fragment>
                ) : (
                  <React.Fragment><PanelLeftOpen className="h-4 w-4 mr-1.5" />Show Details</React.Fragment>
                )}
              </Button>
              <h3 className="text-sm font-medium text-muted-foreground">Preview</h3>
            </div>
            {/* A4 Paper: dimensions depend on orientation — Task 2.3 */}
            <div className="rounded-lg border bg-white shadow-lg" style={{ 
              width: orientation === 'landscape' ? '297mm' : '210mm',
              minHeight: orientation === 'landscape' ? '210mm' : '297mm',
              padding: '20mm',
              fontFamily: FONT_STACKS[fontFamily] ?? fontFamily, // Task 6.6b
            }}>
              <div className="space-y-6">
                {sections.map((section, idx) => {
                  const nextSection = sections[idx + 1];
                  // Task 6.5g — accent-coloured divider before lineItems
                  const isLastHeader =
                    ['project', 'invoice', 'client'].includes(section.id) &&
                    nextSection?.id === 'lineItems';
                  return (
                    <React.Fragment key={section.id}>
                      {renderPreviewSection(section.id)}
                      {isLastHeader && (
                        <hr style={{ borderColor: primaryColor || '#2563eb', borderTopWidth: 2, margin: 0 }} />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>

            {/* ── Backing Sheet — Task 5.3 ── */}
            {builderBackingSheetNode}
          </div>
        </div>
      </div>

      {/* ── Grouped Row Description Dialog ───────────────────────────────────── */}
      <Dialog open={descDialogOpen} onOpenChange={setDescDialogOpen}>
        <DialogContent className="max-w-3xl flex flex-col max-h-[85vh] p-0 gap-0 overflow-hidden">
          {/* ── Header ── */}
          <div className="shrink-0 border-b border-border bg-gradient-to-r from-primary/8 via-accent/20 to-accent/5">
            <div className="px-6 pt-5 pb-3">
              <DialogHeader>
                <DialogTitle className="text-base text-foreground">Configure Grouped Row Description</DialogTitle>
              </DialogHeader>
              <p className="text-xs text-muted-foreground mt-1">
                Build a dynamic label that summarises each grouped row. Insert fields from the panel on the right.
              </p>
            </div>
            {/* Coloured tab bar */}
            <div className="flex gap-0 px-6 border-t border-border/60">
              {(
                [
                  { key: 'timesheet', label: 'Timesheet' },
                  { key: 'expense',   label: 'Expenses'  },
                  { key: 'purchase',  label: 'Purchases' },
                ] as const
              ).map(({ key, label }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setDescActiveTab(key)}
                  className={[
                    'px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-all duration-150',
                    descActiveTab === key
                      ? 'border-primary text-primary bg-primary/5'
                      : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-accent/40',
                  ].join(' ')}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* ── Two-column body ── */}
          {(() => {
            type ActiveGroupBy = 'person' | 'role' | 'phase' | 'task' | 'cost-center';
            const activeGroupBy = groupBy as ActiveGroupBy;
            const allTokens = ['{person}', '{role}', '{phase}', '{task}', '{costCentreName}', '{costCentreCode}', '{qty}', '{subtotal}', '{itemCount}'];

            // Contextual = tokens relevant to the active groupBy + tab
            const contextualTokens: string[] =
              descActiveTab === 'timesheet' ? (GROUP_BY_TOKENS[activeGroupBy] ?? []) :
              descActiveTab === 'expense'   ? EXPENSE_TOKENS :
              PURCHASE_TOKENS;

            const aggregateTokens = ['{qty}', '{subtotal}', '{itemCount}'];

            // Build side panel groups — contextual tokens shown clearly, rest dimmed
            const sidePanelGroups: TokenGroup[] = [
              {
                label: 'Contextual',
                accent: 'primary' as const,
                description: 'Fields available for this row type and grouping',
                tokens: contextualTokens.map((t) => ({
                  token: t,
                  label: GROUP_TOKEN_META[t]?.label ?? t,
                  description: GROUP_TOKEN_META[t]?.description ?? '',
                })),
              },
              {
                label: 'Aggregates',
                accent: 'primary' as const,
                description: 'Group-level totals, always available',
                tokens: aggregateTokens.map((t) => ({
                  token: t,
                  label: GROUP_TOKEN_META[t]?.label ?? t,
                  description: GROUP_TOKEN_META[t]?.description ?? '',
                })),
              },
              ...((() => {
                // "Other fields" — tokens that exist but are not available for this tab, shown dimmed
                const unavailable = allTokens.filter(
                  (t) => !contextualTokens.includes(t) && !aggregateTokens.includes(t),
                );
                if (unavailable.length === 0) return [];
                return [{
                  label: 'Not available',
                  accent: 'warning' as const,
                  description: 'These fields are not available for this row type',
                  tokens: unavailable.map((t) => ({
                    token: t,
                    label: GROUP_TOKEN_META[t]?.label ?? t,
                    description: GROUP_TOKEN_META[t]?.description ?? '',
                    dimmed: true,
                  })),
                }];
              })()),
            ];

            // Pick draft + setter for active tab
            const activeDraft =
              descActiveTab === 'timesheet' ? descDraftTimesheet :
              descActiveTab === 'expense'   ? descDraftExpense :
              descDraftPurchase;
            const setActiveDraft =
              descActiveTab === 'timesheet' ? setDescDraftTimesheet :
              descActiveTab === 'expense'   ? setDescDraftExpense :
              setDescDraftPurchase;

            // Live preview
            const allGroups = groupLineItems(
              mockPreviewData.lineItems,
              groupBy as Parameters<typeof groupLineItems>[1],
            ) ?? [];
            const previewGroup = allGroups.find(g => {
              const type = g.items[0]?.lineItemType ?? (g.items[0]?.person?.trim() ? 'timesheet' : 'expense');
              return type === descActiveTab;
            }) ?? (allGroups.length > 0 ? allGroups[0] : null);
            const rawText = activeDraft.replace(/<[^>]*>/g, '').trim();
            const resolvedPreview = previewGroup && rawText
              ? resolveGroupLabel(rawText, previewGroup.items, {
                  totalQty: previewGroup.totalQty,
                  subtotalAmount: previewGroup.subtotalAmount,
                  itemCount: previewGroup.itemCount,
                })
              : rawText || '—';

            return (
              <div className="flex flex-1 min-h-0">
                {/* Left — editor + preview */}
                <div className="flex-1 flex flex-col min-h-0 overflow-y-auto px-6 py-4 space-y-3">
                  <p className="text-sm text-muted-foreground">
                    {descActiveTab === 'timesheet' && 'Sets the Description cell for timesheet summary rows.'}
                    {descActiveTab === 'expense'   && 'Sets the Description cell for expense summary rows.'}
                    {descActiveTab === 'purchase'  && 'Sets the Description cell for purchase summary rows.'}
                  </p>

                  <GroupedRowDescriptionEditor
                    key={descActiveTab}
                    ref={descEditorRef}
                    content={activeDraft}
                    onChange={setActiveDraft}
                  />

                  {/* Live preview */}
                  <div className="rounded-lg border border-primary/20 bg-primary/5 px-3.5 py-2.5">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-primary/70 mb-1">
                      Live Preview{previewGroup ? ` · ${previewGroup.groupLabel}` : ''}
                    </p>
                    <p className="text-sm text-foreground italic">&ldquo;{resolvedPreview}&rdquo;</p>
                  </div>
                </div>

                {/* Right — token side panel */}
                <TokenSidePanel
                  groups={sidePanelGroups}
                  onInsert={(token) => descEditorRef.current?.insertToken(token)}
                />
              </div>
            );
          })()}

          {/* ── Footer ── */}
          <DialogFooter className="gap-2 px-6 py-4 border-t shrink-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => setDescDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => {
                setGroupedRowDescription(descDraftTimesheet);
                setGroupedRowDescriptionExpense(descDraftExpense);
                setGroupedRowDescriptionPurchase(descDraftPurchase);
                setDescDialogOpen(false);
              }}
            >
              Save Description
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Notes Configure Dialog ─────────────────────────────────────────── */}
      <Dialog open={notesDialogOpen} onOpenChange={setNotesDialogOpen}>
        <DialogContent className="max-w-2xl flex flex-col max-h-[85vh] p-0 gap-0 overflow-hidden">
          <div className="px-6 pt-5 pb-4 border-b border-border shrink-0 bg-gradient-to-r from-primary/8 via-accent/20 to-accent/5">
            <DialogHeader>
              <DialogTitle className="text-base text-foreground">Configure Invoice Notes</DialogTitle>
            </DialogHeader>
            <p className="text-xs text-muted-foreground mt-1">
              Add freeform notes to the invoice footer — supports rich text formatting.
            </p>
          </div>
          <div className="flex-1 overflow-y-auto px-6 py-4">
            <RichTextEditor
              content={notesDraft}
              onChange={setNotesDraft}
              placeholder="Enter invoice notes here — supports bold, italic, lists and links…"
              maxLength={NOTES_MAX_LENGTH}
            />
          </div>
          <DialogFooter className="gap-2 px-6 py-4 border-t shrink-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => setNotesDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => {
                setNotesContent(notesDraft);
                setNotesDialogOpen(false);
              }}
            >
              Save Notes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Terms & Conditions Configure Dialog ────────────────────────────── */}
      <Dialog open={termsDialogOpen} onOpenChange={setTermsDialogOpen}>
        <DialogContent className="max-w-3xl flex flex-col max-h-[85vh] p-0 gap-0 overflow-hidden">
          {/* ── Header ── */}
          <div className="px-6 pt-5 pb-4 border-b border-border shrink-0 bg-gradient-to-r from-primary/8 via-accent/20 to-accent/5">
            <DialogHeader>
              <DialogTitle className="text-base text-foreground">Configure Terms &amp; Conditions</DialogTitle>
            </DialogHeader>
            <p className="text-xs text-muted-foreground mt-1.5">
              Write your terms using rich text. Click a field in the panel on the right to insert a dynamic value that resolves at invoice generation time.
            </p>
          </div>

          {/* ── Two-column body ── */}
          {(() => {
            // Build token groups for the side panel from the full MergeToken list
            const allTokens = getMergeTokens();
            const CATEGORY_ORDER = ['client', 'invoice', 'project', 'company'] as const;
            const CATEGORY_LABELS: Record<string, string> = {
              client: 'Client',
              invoice: 'Invoice',
              project: 'Project',
              company: 'Company',
            };
            const sidePanelGroups: TokenGroup[] = CATEGORY_ORDER
              .map((cat) => ({
                label: CATEGORY_LABELS[cat],
                tokens: allTokens
                  .filter((t) => t.category === cat)
                  .map((t) => ({
                    token: t.token,
                    label: t.label.replace(/^\[|\]$/g, ''),
                    description: t.description,
                  })),
              }))
              .filter((g) => g.tokens.length > 0);

            return (
              <div className="flex flex-1 min-h-0">
                {/* Left — rich text editor + live preview */}
                <div className="flex-1 flex flex-col min-h-0 overflow-y-auto px-6 py-4 space-y-3">
                  <RichTextEditor
                    content={termsDraft}
                    onChange={setTermsDraft}
                    placeholder="Enter your terms and conditions here…"
                    maxLength={TERMS_MAX_LENGTH}
                  />
                  {/* Live preview with sample data substituted for merge tokens */}
                  <div className="rounded-lg border border-primary/20 bg-primary/5 px-3.5 py-2.5">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-primary/70 mb-1.5">Live Preview</p>
                    {termsDraft ? (
                      <div
                        className="prose prose-sm max-w-none text-foreground text-xs"
                        dangerouslySetInnerHTML={{
                          __html: termsDraft
                            .replace(/data-merge-token="[^"]*"\s+class="[^"]*"\s+contenteditable="false"/g, '')
                            .replace(/<span[^>]*>\[Client Name\]<\/span>/g, '<strong style="color:var(--primary)">Acme Corporation Ltd</strong>')
                            .replace(/<span[^>]*>\[Payment Terms Days\]<\/span>/g, '<strong style="color:var(--primary)">30</strong>')
                            .replace(/<span[^>]*>\[Payment Terms Description\]<\/span>/g, '<strong style="color:var(--primary)">Net 30 Days</strong>')
                            .replace(/<span[^>]*>\[Payment Terms Code\]<\/span>/g, '<strong style="color:var(--primary)">NET30</strong>')
                            .replace(/<span[^>]*>\[Invoice Date\]<\/span>/g, `<strong style="color:var(--primary)">${new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</strong>`)
                            .replace(/<span[^>]*>\[Due Date\]<\/span>/g, `<strong style="color:var(--primary)">${new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</strong>`)
                            .replace(/<span[^>]*>\[Project Name\]<\/span>/g, '<strong style="color:var(--primary)">Website Redesign Project</strong>')
                            .replace(/<span[^>]*>\[Company Name\]<\/span>/g, '<strong style="color:var(--primary)">Your Company Name</strong>'),
                        }}
                      />
                    ) : (
                      <p className="text-xs text-muted-foreground italic">Nothing to preview yet — start typing your terms above.</p>
                    )}
                  </div>
                </div>

                {/* Right — token side panel */}
                <TokenSidePanel
                  groups={sidePanelGroups}
                  onInsert={(token) => {
                    // We need to insert the token as a friendly label chip into the draft.
                    // Since RichTextEditor doesn't expose a ref, we update the draft string
                    // by appending the token label — the user can position it by typing around it.
                    // For a seamless experience the MergeFieldEditor chip approach is used via
                    // state: append at end with a space separator.
                    const matchedToken = allTokens.find((t) => t.token === token);
                    if (!matchedToken) return;
                    setTermsDraft((prev) => {
                      const chip = `<span data-merge-token="${matchedToken.token}" class="merge-chip inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium bg-primary/10 text-primary border border-primary/20 select-none cursor-default mx-0.5" contenteditable="false">${matchedToken.label}</span>`;
                      // If content ends with </p>, insert chip before the closing tag
                      if (prev.endsWith('</p>')) {
                        return prev.slice(0, -4) + chip + '</p>';
                      }
                      return prev + chip;
                    });
                  }}
                />
              </div>
            );
          })()}

          {/* ── Footer ── */}
          <DialogFooter className="gap-2 px-6 py-4 border-t shrink-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => setTermsDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => {
                setTermsContent(termsDraft);
                setTermsDialogOpen(false);
              }}
            >
              Save Terms
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
});

// ─────────────────────────────────────────────────────────────────────────────
// SortableColumnRow — Task 2.7c
// A sortable row for a column toggle in the Line Items section.
// ─────────────────────────────────────────────────────────────────────────────
interface SortableColumnRowProps {
  id: string;
  label: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
}

function SortableColumnRow({ id, label, checked, onCheckedChange, disabled = false }: SortableColumnRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : disabled ? 0.4 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 rounded px-1 py-0.5 ${
        disabled
          ? 'opacity-40 cursor-not-allowed'
          : isDragging
          ? 'ring-1 ring-primary bg-muted/50'
          : 'hover:bg-muted/30'
      }`}
    >
      {/* Drag handle */}
      <button
        type="button"
        className={`touch-none focus:outline-none p-0.5 ${disabled ? 'cursor-not-allowed' : 'cursor-grab active:cursor-grabbing'}`}
        {...(disabled ? {} : { ...attributes, ...listeners })}
        aria-label={`Drag to reorder ${label}`}
        disabled={disabled}
        tabIndex={disabled ? -1 : 0}
      >
        <GripVertical className="h-3.5 w-3.5 text-muted-foreground" />
      </button>
      <div className="flex items-center justify-between flex-1 py-0.5">
        <Label
          htmlFor={`col-${id}`}
          className={`text-sm font-normal ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
        >
          {label}
        </Label>
        <Switch
          id={`col-${id}`}
          checked={checked}
          onCheckedChange={onCheckedChange}
          disabled={disabled}
        />
      </div>
    </div>
  );
}

interface FieldToggleProps {
  label: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}

function FieldToggle({ label, checked, onCheckedChange }: FieldToggleProps) {
  return (
    <div className="flex items-center justify-between py-1">
      <Label htmlFor={label} className="cursor-pointer text-sm font-normal">
        {label}
      </Label>
      <Switch
        id={label}
        checked={checked}
        onCheckedChange={onCheckedChange}
      />
    </div>
  );
}

interface SortableSectionProps {
  section: Section;
  expanded: boolean;
  onToggle: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  isFirst: boolean;
  isLast: boolean;
  children: React.ReactNode;
}

function SortableSection({
  section,
  expanded,
  onToggle,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
  children,
}: SortableSectionProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`border rounded-lg ${isDragging ? 'shadow-lg ring-2 ring-primary' : ''}`}
    >
      <div className="flex items-center gap-2 p-4 hover:bg-muted/50 transition-colors">
        {/* Drag Handle */}
        <button
          type="button"
          className="cursor-grab active:cursor-grabbing touch-none focus:outline-none focus:ring-2 focus:ring-primary rounded p-1"
          {...attributes}
          {...listeners}
          aria-label={`Drag to reorder ${section.title}`}
        >
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </button>

        {/* Section Title */}
        <button
          type="button"
          onClick={onToggle}
          className="flex-1 flex items-center justify-between text-left"
        >
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
              {section.title}
            </h3>
          </div>
          {expanded ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
        </button>

        {/* Keyboard Navigation Buttons */}
        <div className="flex gap-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onMoveUp}
            disabled={isFirst}
            className="h-8 w-8 p-0"
            aria-label={`Move ${section.title} up`}
          >
            <ArrowUp className="h-3 w-3" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onMoveDown}
            disabled={isLast}
            className="h-8 w-8 p-0"
            aria-label={`Move ${section.title} down`}
          >
            <ArrowDown className="h-3 w-3" />
          </Button>
        </div>
      </div>

      {expanded && (
        <div className="space-y-3 px-4 pb-4 border-t">
          <div className="pt-4">
            {children}
          </div>
        </div>
      )}
    </div>
  );
}
